import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  MenuItem,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SummarizeIcon from '@mui/icons-material/Summarize';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AssessmentIcon from '@mui/icons-material/Assessment'; // Import AssessmentIcon
import proxyClient from '../utils/proxyClient';
import ChatWidget from '../components/ChatWidget';

// Define OpenAI API interface
interface OpenAIResponse {
  choices: {
    message: {
      content: string;
    };
  }[];
}

// Interface for the deal data we'll fetch from Supabase
interface DealData {
  deal_number: string;
  deal_type: string;
  mtg_type: string;
  ltv: string;
  mortgage_amount: string;
  term: string;
  rate: string;
  amortization: string;
  ltv_restrictions: string; // Note: Field was renamed from ltcRestrictions in UI
  property_info: string;
  borrowers: {
    [key: string]: {
      beacon: string;
      name: string;
      relationship_status: string;
    }
  };
  closing_date: string; // Added closing_date property
}

// Interface for CB Notes data (Aligning with actual fetched data + requirements)
interface CBNotesData {
  name: string;
  beacon: string;
  agent_for_cb: string;
  date_cb_pulled: string;
  warning_noted: string;
  dob_merlin: string;
  dob: string;
  sin_cb_status: string;
  has_aka: string;
  since_date_status: string;
  since_date_other_notes?: string;
  address: string;
  inquiries: string;
  mtg_inquiry_reason: string;
  other_mtg_inquiry_reason?: string;
  employment_on_cb: string;
  pr_rt: string;
  rt_description: string;
  repayment_history: string;
  repayment_history_other: string;
  adverse_media: string;
}

// Interface for borrower notes
interface BorrowerNotes {
  originalNotes: string;
  enhancedNotes: string;
  isSaving: boolean;
  isEditing?: boolean; // Added isEditing property
  dateCbPulled?: string;
  sinceDateStatus?: string;
  sinceDateOtherNotes?: string;
}

// Interface for I/R summary
interface IRSummary {
  originalSummary: string;
  enhancedSummary: string;
  isGenerating: boolean;
  isEditing?: boolean;
  isSaving?: boolean;
}

// Define type for the summary response
interface SummaryResponse {
  summary_text: string;
  deal_number?: string;
  id?: string;
}

// Interface for incomes data
interface IncomesData {
  borrower_id: string;
  income_description: string;
}

const UWNotesPage = () => {
  const [dealNumber, setDealNumber] = useState('');
  const [dealInfo, setDealInfo] = useState<any>({});  // Add dealInfo state
  const [borrowers, setBorrowers] = useState<any[]>([]); // Add borrowers state
  const [summaryText, setSummaryText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [borrowersNotes, setBorrowersNotes] = useState<{[key: string]: BorrowerNotes}>({});
  const [irSummaries, setIRSummaries] = useState<{[key: string]: IRSummary}>({});

  // State for original Deal Summary text during edit
  const [originalSummaryText, setOriginalSummaryText] = useState<string>('');

  // State for Preliminary Check text
  const [prelimCheckText, setPrelimCheckText] = useState<string>('');
  const [originalPrelimCheckText, setOriginalPrelimCheckText] = useState<string>('');
  const [prelimCheckSaveStatus, setPrelimCheckSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [prelimCheckError, setPrelimCheckError] = useState<string | null>(null);

  // State for Rental Income Reasonability
  const [rentalReasonabilityOriginal, setRentalReasonabilityOriginal] = useState<string>('');
  const [rentalReasonabilityEnhanced, setRentalReasonabilityEnhanced] = useState<string>('');
  const [originalRentalReasonability, setOriginalRentalReasonability] = useState<string>(''); // For cancel edit
  const [rentalReasonabilitySaveStatus, setRentalReasonabilitySaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [rentalReasonabilityGenerating, setRentalReasonabilityGenerating] = useState<boolean>(false);
  const [rentalReasonabilityError, setRentalReasonabilityError] = useState<string | null>(null);

  // State for Downpayment Reasonability
  const [downpaymentReasonabilityOriginal, setDownpaymentReasonabilityOriginal] = useState<string>('');
  const [downpaymentReasonabilityEnhanced, setDownpaymentReasonabilityEnhanced] = useState<string>('');
  const [originalDownpaymentReasonability, setOriginalDownpaymentReasonability] = useState<string>(''); // For cancel edit
  const [downpaymentReasonabilitySaveStatus, setDownpaymentReasonabilitySaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [downpaymentReasonabilityGenerating, setDownpaymentReasonabilityGenerating] = useState<boolean>(false);
  const [downpaymentReasonabilityError, setDownpaymentReasonabilityError] = useState<string | null>(null);

  // State for notes and errors (CB Notes - might be removable if not used elsewhere)
  const [notes, setNotes] = useState<string>('');
  const [savingNotes, setSavingNotes] = useState(false);

  // State for I/R Summary and errors
  const [irSummary, setIrSummary] = useState<string>('');
  const [savingIrSummary, setSavingIrSummary] = useState(false);

  const [editMode, setEditMode] = useState<{ [key: string]: boolean }>({});
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);

  // State for rental properties
  const [rentalProperties, setRentalProperties] = useState<any[]>([]);

  // State for Conditions
  const [conditionsText, setConditionsText] = useState<string>('');
  const [editingConditions, setEditingConditions] = useState<boolean>(false);
  const [originalConditionsText, setOriginalConditionsText] = useState<string>('');
  const [conditionsSaveStatus, setConditionsSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [conditionsGenerating, setConditionsGenerating] = useState<boolean>(false);
  const [conditionsError, setConditionsError] = useState<string | null>(null);

  // Add this after the existing state declarations
  
  // Debug useEffect to track downpayment reasonability state changes
  useEffect(() => {
    console.log('DP state changed:', {
      original: downpaymentReasonabilityOriginal,
      enhanced: downpaymentReasonabilityEnhanced
    });
    
    // Add cleanup function to help debug when component is unmounting/remounting
    return () => {
      console.log('DP state tracking effect cleanup - component unmounting or deps changing');
    };
  }, [downpaymentReasonabilityOriginal, downpaymentReasonabilityEnhanced]);

  // Function to generate the summary text from deal data
  const generateSummaryText = (data: DealData, incomesData: IncomesData[]): string => {
    // Helper function to ensure we have a value or a default
    const formatValue = (value: string | undefined | null, defaultVal: string = 'N/A'): string => {
      return value ? String(value).trim() : defaultVal;
    };

    // Generate income description string for borrowers
    const borrowerIncomeDescriptionsString = Object.keys(data.borrowers)
      .map((borrowerKey, index) => {
        const borrowerNumber = index + 1; // For B1, B2 label

        // Find the income record in incomesData that matches this borrowerKey
        // Assumes incomesData items have a 'borrower_id' that matches borrowerKey (e.g., 'borrower1')
        // and an 'income_description' field.
        const incomeRecord = incomesData.find(inc => inc.borrower_id === borrowerKey);

        let incomeDesc = 'N/A'; // Default if no income record or description found
        if (incomeRecord && incomeRecord.income_description) {
          incomeDesc = formatValue(incomeRecord.income_description, 'N/A');
        }
        return `B${borrowerNumber} ${incomeDesc}`;
      })
      .join(', ');

    // Get beacon scores for all borrowers
    const beaconScores = Object.values(data.borrowers)
      .map(borrower => formatValue(borrower.beacon))
      .join(', ');
    
    // Determine borrower relationship text from all borrowers
    const relationshipStatuses = Object.values(data.borrowers)
      .map(borrower => formatValue(borrower.relationship_status)) 
      .filter(status => status && status !== 'N/A') 
      .join(', '); 
    const relationshipText = relationshipStatuses || 'N/A';
    
    // Format mortgage amount with commas if it's a number
    let formattedAmount = formatValue(data.mortgage_amount);
    if (!isNaN(Number(formattedAmount))) {
      formattedAmount = Number(formattedAmount).toLocaleString();
    }

    // Construct the FICO part, prepending borrower income descriptions if available
    let ficoAndIncomePart = `Fico ${beaconScores}`;
    if (borrowerIncomeDescriptionsString) {
      ficoAndIncomePart = `${borrowerIncomeDescriptionsString}, Fico ${beaconScores}`;
    }

    // Construct the formatted summary string with better handling of missing values
    return `${formatValue(data.mtg_type)}, ${formatValue(data.ltv)}% LTV, ${formatValue(data.deal_type)} $${formattedAmount}, ${ficoAndIncomePart}, Ratios in line, ${formatValue(data.term)} Term, ${formatValue(data.rate)}% Rate, ${formatValue(data.amortization)}Y AM, Bs: ${relationshipText}, LTV Restrictions: ${formatValue(data.ltv_restrictions)}, ${formatValue(data.property_info)}`;
  };

  // Function to generate Preliminary Check text
  const generatePrelimCheckText = (dealInfo: any, borrowers: any): string => {
    let text = "Preliminary check completed. Ok to proceed. See notes below:";

    // Helper to append with newline and bullet
    const appendNote = (label: string, value: string | null | undefined) => {
      if (value && String(value).trim()) {
        text += `\n- ${label}: ${String(value).trim()}`;
      }
    };

    appendNote("Property Watchlist Status", dealInfo?.property_watchlist_status);
    appendNote("Employer Watchlist Status", dealInfo?.employer_watchlist_status);

    // Match Files Status and Notes
    let matchFileText = dealInfo?.match_files_status || 'N/A';
    if (dealInfo?.match_files_notes && String(dealInfo.match_files_notes).trim()) {
      matchFileText += ` - Notes: ${String(dealInfo.match_files_notes).trim()}`;
    }
    appendNote("Match Files", matchFileText);

    appendNote("Property Tax Adjustment", dealInfo?.property_tax_adjustment);

    // Borrower Relationships
    const relationshipStatuses = Object.values(borrowers)
      .map((b: any) => b.relationship_status || 'N/A') 
      .filter(status => status && status !== 'N/A') 
      .join(' / '); 
    appendNote("Borrower Relationships", relationshipStatuses || 'N/A');

    text += "\n\nOther notes:";

    // Property Value Status and Notes
    let propValueText = dealInfo?.property_value_status || 'N/A';
    if (dealInfo?.property_value_other_notes && String(dealInfo.property_value_other_notes).trim()) {
      propValueText = String(dealInfo.property_value_other_notes).trim();
    }
    appendNote("Property Value", propValueText);
    
    // Add more notes here if needed in the future

    return text;
  };

  // Function to generate CB Notes for a borrower
  const generateCBNotes = (data: CBNotesData): string => {
    // Check DOB match status
    const dobStatus = data.dob === data.dob_merlin ? "DOB Match" : "DOB Mismatch";
    
    // Determine SIN status text based on sin_cb_status
    let sinStatus = "";
    if (data.sin_cb_status === "Match") {
      sinStatus = "SIN Matches";
    } else if (data.sin_cb_status === "Yes" || data.sin_cb_status === "Not Reporting") {
      sinStatus = "Solicitor to confirm SIN number";
    } else {
       sinStatus = data.sin_cb_status || "Status Unknown"; // Handle other cases or null
    }
    
    // Determine AKA status
    const akaStatus = data.has_aka === "No" 
      ? "Name Matches" 
      : "Solicitor to confirm if name and AKA are same person";
    
    // Combine DOB/SIN/AKA info
    const identityCheck = `${dobStatus}, ${sinStatus}, ${akaStatus}`;

    // Format Since Date
    let sinceDateText = data.since_date_status || "";
    if (data.since_date_status === "Other" && data.since_date_other_notes) {
       sinceDateText += `: ${data.since_date_other_notes}`;
    } else if (data.since_date_other_notes) {
       sinceDateText += ` (${data.since_date_other_notes})`;
    }
    
    // Determine repayment history text
    let repaymentText = "";
    switch (data.repayment_history) {
      case "Other":
        repaymentText = data.repayment_history_other || "Custom repayment history not specified";
        break;
      default:
        repaymentText = data.repayment_history || "Repayment history not specified"; // Use value directly if not standard
    }
    
    // Format inquiries - Combine standard reason and other reason if present
    let inquiriesText = data.inquiries?.toLowerCase() || 'none';
    inquiriesText += ', ';
    inquiriesText += data.mtg_inquiry_reason || data.other_mtg_inquiry_reason || 'none';
    
    // Construct the formatted CB notes using corrected field names
    // Move Beacon to the same line as Credit For
    return `Credit For: ${data.name || 'N/A'}, Beacon: ${data.beacon || 'N/A'}, 
Status of Agent who pulled CB: ${data.agent_for_cb || 'N/A'}, 
CB Date: ${data.date_cb_pulled || 'N/A'}, 
Warnings or Alerts: ${data.warning_noted || 'None'}, 
Client DOB, SIN, Name on CB: ${identityCheck}, 
Since Date Makes Sense: ${sinceDateText || 'N/A'}, 
Address on CB: ${data.address || 'N/A'}, 
# Inquiries: ${inquiriesText}, 
Employment on CB: ${data.employment_on_cb || 'N/A'}, 
Public Records/Registered Trades: ${data.pr_rt || 'None'}, ${data.rt_description || 'None'}, 
Repayment History: ${repaymentText}, 
Web Search of Client Shows: ${data.adverse_media || 'None'}`;
  };

  // Function to enhance CB Notes with OpenAI API
  const enhanceCBNotes = async (notes: string): Promise<string> => {
    try {
      // Use proxyClient instead of direct fetch
      const { data, error } = await proxyClient.post('/api/borrower-data/openai', {
        prompt: `Please rewrite the following borrower notes to be more concise and professional while retaining all important information: ${notes}`,
        type: 'uw_notes' // Specify the type for better context
      });

      if (error) {
        throw new Error('Failed to enhance notes: ' + error);
      }

      return data.text;
    } catch (error) {
      console.error('Error enhancing notes:', error);
      throw error;
    }
  };

  // Function to save borrower notes to Supabase
  const saveBorrowerNotes = async (borrowerIndex: number, originalNotes: string, enhancedNotes: string) => {
    try {
      // Use the dealNumber from component state instead of trying to get it from DOM
      if (!dealNumber) {
        throw new Error('Deal number is required');
      }
      
      const { data, error } = await proxyClient.post('/api/borrower-data/cb-notes', {
        dealNumber,
        borrowerNumber: borrowerIndex,
        originalNotes,
        enhancedNotes
      });
      
      if (error) {
        throw new Error('Failed to save notes: ' + error);
      }
      
      return true;
    } catch (error) {
      console.error('Error saving borrower notes:', error);
      return false;
    }
  };

  // Handle borrower notes change
  const handleBorrowerNotesChange = (borrowerKey: string, noteType: 'originalNotes' | 'enhancedNotes' | 'dateCbPulled' | 'sinceDateStatus' | 'sinceDateOtherNotes', value: string) => {
    setBorrowersNotes(prev => ({
      ...prev,
      [borrowerKey]: {
        ...prev[borrowerKey],
        [noteType]: value,
        isSaving: false,
      },
    }));
  };

  // Function to save the summary text back to Supabase
  const saveSummaryText = async () => {
    if (!dealNumber || !summaryText) return;
    
    setSaveStatus('saving');
    try {
      const { data, error } = await proxyClient.post('/api/borrower-data/uw-summaries', {
        dealNumber,
        summaryText
      });
      
      if (error) {
        throw new Error('Failed to save summary: ' + error);
      }
      
      setSaveStatus('saved');
      
      // Reset status after 3 seconds
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (err) {
      console.error('Error saving summary:', err);
      setSaveStatus('error');
      setError('Failed to save summary text. Please try again.');
      
      // Reset status after 3 seconds
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  // Function to generate the IR summary text for a borrower
  const generateIRSummary = async (borrowerData: any, dealMatchStatus: string | null, dealMatchNotes: string | null): Promise<string> => {
    try {
      // Make sure we have both borrower_id and borrower_number for compatibility
      if (!borrowerData.borrower_id && borrowerData.id) {
        borrowerData.borrower_id = borrowerData.id;
      }
      
      if (!borrowerData.borrower_number && borrowerData.number) {
        borrowerData.borrower_number = borrowerData.number;
      }
      
      console.log('Sending borrower data to generate IR summary:', {
        id: borrowerData.id,
        borrower_id: borrowerData.borrower_id,
        borrower_number: borrowerData.borrower_number,
        deal_number: borrowerData.deal_number,
        name: borrowerData.name
      });
      
      // Use proxyClient instead of direct fetch
      const { data, error } = await proxyClient.post('/api/borrower-data/generate-ir-summary', {
        borrowerData,
        dealMatchStatus,
        dealMatchNotes
      });
      
      if (error) {
        throw new Error('Failed to generate IR summary: ' + error);
      }
      
      return data.summary;
    } catch (error) {
      console.error('Error generating IR summary:', error);
      // Fallback to a basic template if there's an error
      return `${borrowerData.name || 'Borrower'}'s Income Reasonability Summary: [Add details about borrower's income sources, employment history, and any concerns or notes about income reasonability]`;
    }
  };

  // Function to enhance IR summary with OpenAI API
  const enhanceIRSummary = async (summary: string): Promise<string> => {
    try {
      // Use proxyClient instead of direct fetch
      const { data, error } = await proxyClient.post('/api/borrower-data/openai', {
        prompt: `Please rewrite the following income reasonability summary to be more concise and professional while keeping all important information: ${summary}`,
        type: 'uw_notes' // Specify the type for better context
      });

      if (error) {
        throw new Error('Failed to enhance summary: ' + error);
      }

      return data.text;
    } catch (error) {
      console.error('Error enhancing IR summary:', error);
      throw error;
    }
  };

  // Function to save IR summaries to Supabase
  const saveIRSummary = async (borrowerIndex: number, originalSummary: string, enhancedSummary: string) => {
    if (!dealNumber) return;
    
    try {
      // Use proxyClient instead of direct fetch
      const { error } = await proxyClient.post('/api/borrower-data/ir-summaries', {
        dealNumber,
        borrowerNumber: borrowerIndex,
        originalSummary,
        enhancedSummary
      });
      
      if (error) {
        throw new Error('Failed to save IR summary: ' + error);
      }
      
      return true;
    } catch (err) {
      console.error(`Error saving IR summary for borrower ${borrowerIndex}:`, err);
      return false;
    }
  };

  // Handle Generate button click for IR summary
  const handleGenerateEnhancedIR = async (borrowerKey: string) => {
    const summary = irSummaries[borrowerKey];
    if (!summary?.originalSummary) return;
    
    // Set generating state
    setIRSummaries(prev => ({
      ...prev,
      [borrowerKey]: {
        ...prev[borrowerKey],
        isGenerating: true
      }
    }));
    
    try {
      // Call OpenAI to enhance the summary
      const enhancedSummary = await enhanceIRSummary(summary.originalSummary);
      
      // Update state with enhanced summary
      setIRSummaries(prev => ({
        ...prev,
        [borrowerKey]: {
          ...prev[borrowerKey],
          enhancedSummary,
          isGenerating: false
        }
      }));
      
      // Save to Supabase
      const borrowerNumber = parseInt(borrowerKey.replace('borrower', ''));
      await saveIRSummary(borrowerNumber, summary.originalSummary, enhancedSummary);
      
    } catch (err) {
      console.error('Error generating enhanced IR summary:', err);
      
      // Reset generating state
      setIRSummaries(prev => ({
        ...prev,
        [borrowerKey]: {
          ...prev[borrowerKey],
          isGenerating: false
        }
      }));
    }
  };
  
  // Handle IR summary change
  const handleIRSummaryChange = (borrowerKey: string, summaryType: 'originalSummary' | 'enhancedSummary', value: string) => {
    setIRSummaries(prev => ({
      ...prev,
      [borrowerKey]: {
        ...prev[borrowerKey],
        [summaryType]: value,
      },
    }));
  };

  // Function to handle pulling deal information
  const handlePullInfo = async () => {
    if (!dealNumber) {
      setError('Please enter a deal number');
      return;
    }

    setIsLoading(true);
    setShowSummary(false);
    setError(null);
    setBorrowersNotes({});
    setIRSummaries({});
    
    // Don't clear these values at the beginning, we'll set them based on API response
    // setDownpaymentReasonabilityOriginal('');
    // setDownpaymentReasonabilityEnhanced('');

    try {
      // Get saved summary text, if any
      let savedSummary: SummaryResponse | null = null;
      const { data: summaryData, error: summaryError } = await proxyClient.get(`/api/borrower-data/uw-summaries/${dealNumber}`);
      
      if (summaryError) {
        console.error('Error fetching summary:', summaryError);
      } else if (summaryData) {
        savedSummary = summaryData as SummaryResponse;
        
        if (savedSummary && savedSummary.summary_text) {
          setSummaryText(savedSummary.summary_text);
          setShowSummary(true);
        }
      }

      // Get deal data
      const { data: dealData, error: dealError } = await proxyClient.get(`/api/borrower-data/deals/${dealNumber}`);
      
      if (dealError) {
        throw new Error('Failed to fetch deal information: ' + dealError);
      }
      
      if (!dealData || dealData.length === 0) {
        throw new Error('Deal not found');
      }
      
      const dealInfo = dealData[0];
      console.log('DEBUG: Fetched dealInfo:', JSON.stringify(dealInfo, null, 2)); // Added log
      setDealInfo(dealInfo);

      // Add debug logs to check for downpayment fields
      console.log('Deal info for downpayment template:', {
        downpayment_source: dealInfo.downpayment_source,
        giftor: dealInfo.giftor,
        being_sold_for_dp: dealInfo.being_sold_for_dp,
        rental_property_address: dealInfo.rental_property_address,
        downpayment_source_notes: dealInfo.downpayment_source_notes
      });

      // Fetch rental properties
      try {
        console.log('Fetching rental properties for deal:', dealNumber);
        const { data: rentalPropertiesData, error: rentalPropertiesError } = await proxyClient.get(`/api/borrower-data/rental-properties/${dealNumber}`);
        
        console.log('Rental properties API response:', { 
          data: rentalPropertiesData, 
          error: rentalPropertiesError 
        });
        
        // If we get rental properties data, add the address to the dealInfo object
        if (!rentalPropertiesError && rentalPropertiesData && rentalPropertiesData.length > 0) {
          // Save rental properties to state
          setRentalProperties(rentalPropertiesData);
          
          // Find a property being sold for DP if possible
          const soldForDpProperty = rentalPropertiesData.find(prop => 
            prop.being_sold_for_dp === true || 
            prop.being_sold_for_dp === 'true' || 
            prop.being_sold_for_dp === 'TRUE' || 
            prop.being_sold_for_dp === 'yes' || 
            prop.being_sold_for_dp === 'Yes'
          );
          
          if (soldForDpProperty) {
            dealInfo.rental_property_address = soldForDpProperty.address;
            console.log('Found rental property being sold for DP with address:', dealInfo.rental_property_address);
          } else if (rentalPropertiesData[0].address) {
            // Fallback to first property if none marked as sold for DP
            dealInfo.rental_property_address = rentalPropertiesData[0].address;
            console.log('Using first rental property address:', dealInfo.rental_property_address);
          }
        }
      } catch (err) {
        console.error('Error fetching rental properties:', err);
      }

      // Generate and set preliminary check text
      const prelimText = generatePrelimCheckText(dealInfo, dealInfo.borrowers || {});
      setPrelimCheckText(prelimText);

      // Check for existing downpayment reasonability notes
      console.log('Checking for existing downpayment reasonability notes for deal:', dealNumber);
      const { data: existingDownpaymentData, error: downpaymentError } = await proxyClient.get(`/api/borrower-data/downpayment-reasonability/${dealNumber}`);

      console.log('Downpayment reasonability API response:', { 
        data: existingDownpaymentData, 
        error: downpaymentError 
      });

      if (!downpaymentError && existingDownpaymentData && existingDownpaymentData.original_text) {
        // Use existing data if available
        console.log('Using existing downpayment reasonability notes');
        const dpOriginal = existingDownpaymentData.original_text;
        const dpEnhanced = existingDownpaymentData.enhanced_text || '';
        
        // Set the values explicitly
        setDownpaymentReasonabilityOriginal(dpOriginal);
        setDownpaymentReasonabilityEnhanced(dpEnhanced);
        
        console.log('Set downpayment state values:', {
          original: dpOriginal,
          enhanced: dpEnhanced
        });
        
        // Force state check after a small delay
        setTimeout(() => {
          console.log('Delayed state check:', {
            original: downpaymentReasonabilityOriginal,
            enhanced: downpaymentReasonabilityEnhanced
          });
        }, 500);
      } else {
        // Generate new downpayment reasonability text
        console.log('Generating new downpayment reasonability text');
        const downpaymentText = generateDownpaymentReasonabilityText(dealInfo);
        setDownpaymentReasonabilityOriginal(downpaymentText);
        
        // Save the downpayment reasonability text to the database if not empty
        if (downpaymentText) {
          console.log('Saving generated downpayment reasonability text:', downpaymentText);
          try {
            const { error: saveError } = await proxyClient.post('/api/borrower-data/downpayment-reasonability', {
              dealNumber,
              originalText: downpaymentText,
              enhancedText: ''  // Start with empty enhanced text
            });
            
            if (saveError) {
              console.error('Error saving downpayment reasonability:', saveError);
            } else {
              console.log('Successfully saved downpayment reasonability text to database');
            }
          } catch (err) {
            console.error('Error saving downpayment reasonability:', err);
          }
        } else {
          console.log('No downpayment text generated, nothing to save');
        }
      }
      
      // Get borrower data
      const { data: borrowersData, error: borrowersError } = await proxyClient.get(`/api/borrower-data/borrowers/${dealNumber}`);
      
      if (borrowersError) {
        throw new Error('Failed to fetch borrower information: ' + borrowersError);
      }
      
      console.log('DEBUG: Fetched borrowersData:', JSON.stringify(borrowersData, null, 2)); // Added log
      setBorrowers(borrowersData || []);
      
      // Prepare borrower notes structure
      const notesObj: { [key: string]: BorrowerNotes } = {};
      const summariesObj: { [key: string]: IRSummary } = {};
      
      // Initialize notes structure for each borrower
      for (const borrower of borrowersData) {
        const borrowerKey = `borrower${borrower.borrower_number}`;
        notesObj[borrowerKey] = { 
          originalNotes: '', 
          enhancedNotes: '', 
          isSaving: false 
        };
        summariesObj[borrowerKey] = { 
          originalSummary: '', 
          enhancedSummary: '', 
          isGenerating: false 
        };
      }
      
      // Fetch CB notes for each borrower
      for (const borrower of borrowersData) {
        try {
          const borrowerNumber = borrower.borrower_number;
          const borrowerKey = `borrower${borrowerNumber}`;
          
          console.log(`Fetching data for borrower ${borrowerNumber}, deal ${dealNumber}`);
          
          // Ensure borrower data has the fields needed for generation
          const borrowerWithFields = {
            ...borrower,
            id: borrower.id, // Make sure id is included
            borrower_id: borrower.id, // Add borrower_id for consistency with new code
            number: borrower.borrower_number, // Ensure number field exists
            borrower_number: borrower.borrower_number,
            deal_number: dealNumber,
            name: borrower.full_name || borrower.name || '',
          };
          
          // Add debug info about the borrower data
          console.log(`Borrower data for API calls:`, borrowerWithFields);
          
          const { data: cbData, error: cbError } = await proxyClient.get(`/api/borrower-data/cb-notes/${dealNumber}/${borrowerNumber}`);
          
          console.log('CB notes response:', { data: cbData, error: cbError });
          
          if (!cbError && cbData) {
            notesObj[borrowerKey] = {
              ...notesObj[borrowerKey],
              originalNotes: cbData.original_notes || '',
              enhancedNotes: cbData.enhanced_notes || ''
            };
          } else {
            console.log(`No CB notes found for borrower ${borrowerNumber}, initializing with empty values`);
            
            // Try to generate CB notes if none exist
            try {
              // Only proceed if we have enough data to generate notes
              if (borrowerWithFields.name && borrowerWithFields.beacon) {
                console.log('Attempting to generate new CB notes from borrower data');
                
                // Generate original notes from borrower data
                const generatedNotes = generateCBNotes(borrowerWithFields);
                
                // Initialize with generated notes but empty enhanced notes
                notesObj[borrowerKey] = {
                  ...notesObj[borrowerKey],
                  originalNotes: generatedNotes,
                  enhancedNotes: ''
                };
                
                console.log('Generated new CB notes successfully');
                
                // Save the generated notes to the database
                try {
                  const saveResult = await saveBorrowerNotes(borrowerNumber, generatedNotes, '');
                  console.log(`Saved generated CB notes to database: ${saveResult ? 'success' : 'failed'}`);
                } catch (saveError) {
                  console.error('Error saving generated CB notes:', saveError);
                }
              } else {
                console.log('Insufficient borrower data to generate CB notes');
                // Make sure we always have an entry even if no data is returned
                notesObj[borrowerKey] = {
                  ...notesObj[borrowerKey],
                  originalNotes: '',
                  enhancedNotes: ''
                };
              }
            } catch (genError) {
              console.error('Error generating CB notes:', genError);
              // Make sure we always have an entry even if generation fails
              notesObj[borrowerKey] = {
                ...notesObj[borrowerKey],
                originalNotes: '',
                enhancedNotes: ''
              };
            }
          }
          
          // Fetch IR summaries
          const { data: irData, error: irError } = await proxyClient.get(`/api/borrower-data/ir-summaries/${dealNumber}/${borrowerNumber}`);
          
          console.log('IR summaries response:', { data: irData, error: irError });
          
          if (!irError && irData) {
            summariesObj[borrowerKey] = {
              ...summariesObj[borrowerKey],
              originalSummary: irData.original_summary || '',
              enhancedSummary: irData.enhanced_summary || ''
            };
          } else {
            console.log(`No IR summaries found for borrower ${borrowerNumber}, initializing with empty values`);
            
            // Try to generate IR summary if none exists
            try {
              // Only proceed if we have enough data
              if (borrowerWithFields.name) {
                console.log('Attempting to generate new IR summary from borrower data');
                
                // Get match file info from deal data
                const dealMatchStatus = dealInfo.match_files_status || null;
                const dealMatchNotes = dealInfo.match_files_notes || null;
                
                // Generate IR summary
                const generatedSummary = await generateIRSummary(borrowerWithFields, dealMatchStatus, dealMatchNotes);
                
                // Initialize with generated summary but empty enhanced summary
                summariesObj[borrowerKey] = {
                  ...summariesObj[borrowerKey],
                  originalSummary: generatedSummary,
                  enhancedSummary: ''
                };
                
                console.log('Generated new IR summary successfully');
                
                // Save the generated IR summary to the database
                try {
                  const saveResult = await saveIRSummary(borrowerNumber, generatedSummary, '');
                  console.log(`Saved generated IR summary to database: ${saveResult ? 'success' : 'failed'}`);
                } catch (saveError) {
                  console.error('Error saving generated IR summary:', saveError);
                }
              } else {
                console.log('Insufficient borrower data to generate IR summary');
                // Make sure we always have an entry even if no data is returned
                summariesObj[borrowerKey] = {
                  ...summariesObj[borrowerKey],
                  originalSummary: '',
                  enhancedSummary: ''
                };
              }
            } catch (genError) {
              console.error('Error generating IR summary:', genError);
              // Make sure we always have an entry even if generation fails
              summariesObj[borrowerKey] = {
                ...summariesObj[borrowerKey],
                originalSummary: '',
                enhancedSummary: ''
              };
            }
          }
        } catch (err) {
          console.error(`Error fetching data for borrower ${borrower.borrower_number}:`, err);
        }
      }
      
      console.log('Final borrower notes:', notesObj);
      console.log('Final IR summaries:', summariesObj);
      
      setBorrowersNotes(notesObj);
      setIRSummaries(summariesObj);
      
      // Fetch rental reasonability
      try {
        console.log('Fetching rental reasonability for deal:', dealNumber);
        const { data: rentalData, error: rentalError } = await proxyClient.get(`/api/borrower-data/rental-reasonability/${dealNumber}`);
        
        if (!rentalError && rentalData && rentalData.original_text) {
          setRentalReasonabilityOriginal(rentalData.original_text || '');
          setRentalReasonabilityEnhanced(rentalData.enhanced_text || '');
        } else {
          // If no existing data, auto-generate the notes
          console.log('No rental reasonability data found, auto-generating...');
          try {
            const { data: generatedData, error: generateError } = await proxyClient.get(`/api/borrower-data/auto-generate-rental-reasonability/${dealNumber}`);
            
            if (!generateError && generatedData && generatedData.original_text) {
              setRentalReasonabilityOriginal(generatedData.original_text);
              setRentalReasonabilityEnhanced('');
              console.log('Successfully auto-generated rental reasonability notes');
            } else {
              console.log('No qualifying rental properties found or error generating notes');
              setRentalReasonabilityOriginal('');
              setRentalReasonabilityEnhanced('');
            }
          } catch (genError) {
            console.error('Error auto-generating rental reasonability:', genError);
            setRentalReasonabilityOriginal('');
            setRentalReasonabilityEnhanced('');
          }
        }
      } catch (err) {
        console.error('Error fetching rental reasonability:', err);
        // Don't let this error block the rest of the function
        setRentalReasonabilityOriginal('');
        setRentalReasonabilityEnhanced('');
      }
      
      // Fetch downpayment reasonability
      try {
        // Skip this API call as it's not set up yet
        // Comment out the problematic code
        /*
        const { data: downpaymentData, error: downpaymentError } = await proxyClient.get(`/api/borrower-data/downpayment-reasonability/${dealNumber}`);
        
        if (!downpaymentError && downpaymentData) {
          setDownpaymentReasonabilityOriginal(downpaymentData.original_text || '');
          setDownpaymentReasonabilityEnhanced(downpaymentData.enhanced_text || '');
        }
        */
        // REMOVE THIS - Don't reset state values since we already handled them earlier
        // setDownpaymentReasonabilityOriginal('');
        // setDownpaymentReasonabilityEnhanced('');
      } catch (err) {
        console.error('Error fetching downpayment reasonability:', err);
        // Don't let this error block the rest of the function
        // But also don't reset the values
        // setDownpaymentReasonabilityOriginal('');
        // setDownpaymentReasonabilityEnhanced('');
      }
      
      // Fetch preliminary checks
      try {
        const { data: prelimCheckData, error: prelimCheckError } = await proxyClient.get(`/api/borrower-data/preliminary-checks/${dealNumber}`);
        
        if (!prelimCheckError && prelimCheckData) {
          setPrelimCheckText(prelimCheckData.check_text || '');
        } else {
          console.log('No preliminary check data found, generating...');
          
          // Generate preliminary check text using deal data
          const generatedPrelimCheck = generatePrelimCheckText(dealInfo, borrowersData);
          setPrelimCheckText(generatedPrelimCheck);
          
          // Save the generated preliminary check to the database
          try {
            const { error } = await proxyClient.post('/api/borrower-data/preliminary-checks', {
              dealNumber,
              checkText: generatedPrelimCheck
            });
            
            if (error) {
              console.error('Error saving generated preliminary check:', error);
            } else {
              console.log('Generated preliminary check saved successfully');
            }
          } catch (saveError) {
            console.error('Error saving generated preliminary check:', saveError);
          }
        }
      } catch (err) {
        console.error('Error fetching preliminary checks:', err);
      }
      
      // If no saved summary exists, generate one from deal data
      if (!savedSummary || !savedSummary.summary_text) {
        // Format the deal data for the summary generator
        const dealForSummary: DealData = {
          deal_number: dealInfo.deal_number || '',
          deal_type: dealInfo.deal_type || '',
          mtg_type: dealInfo.mtg_type || '',
          ltv: dealInfo.ltv || '',
          mortgage_amount: dealInfo.mortgage_amount || '',
          term: dealInfo.term || '',
          rate: dealInfo.rate || '',
          amortization: dealInfo.amortization || '',
          ltv_restrictions: dealInfo.ltv_restrictions || '',
          property_info: dealInfo.property_info || '',
          closing_date: dealInfo.closing_date || '',
          borrowers: {}
        };
        
        // Format borrowers for the summary generator
        borrowersData.forEach((borrower: any) => {
          const key = `borrower${borrower.borrower_number}`;
          dealForSummary.borrowers[key] = {
            beacon: borrower.beacon || '',
            name: borrower.name || '',
            relationship_status: borrower.relationship_status || ''
          };
        });
        
        // Re-fetch incomesData using the current dealNumber
        const { data: incomesData, error: incomesError } = await proxyClient.get(`/api/borrower-data/incomes/${dealNumber}`);
        
        if (incomesError) {
          console.error('Error fetching incomes:', incomesError);
        }
        
        console.log('DEBUG: Fetched incomesData for summary generation:', JSON.stringify(incomesData, null, 2)); // Added log
        console.log('DEBUG: dealForSummary object before generating summary:', JSON.stringify(dealForSummary, null, 2)); // Added log
        
        // Generate summary text using existing function
        const generatedSummary = generateSummaryText(dealForSummary, incomesData);
        setSummaryText(generatedSummary);
        setShowSummary(true);
      }
      
      // Fetch existing conditions text
      try {
        console.log('Generating conditions for deal:', dealNumber);
        
        // First get borrowers data
        const { data: borrowersData, error: borrowersError } = await proxyClient.get(`/api/borrower-data/borrowers/${dealNumber}`);
        
        if (borrowersError) {
          console.error('Error fetching borrowers for conditions:', borrowersError);
        } else {
          // Get incomes data
          const { data: incomesData, error: incomesError } = await proxyClient.get(`/api/borrower-data/incomes/${dealNumber}`);
          
          if (incomesError) {
            console.error('Error fetching incomes for conditions:', incomesError);
          } else {
            // Get rental properties data
            const { data: rentalPropertiesData, error: propertiesError } = await proxyClient.get(`/api/borrower-data/rental-properties/${dealNumber}`);
            
            if (propertiesError) {
              console.error('Error fetching rental properties for conditions:', propertiesError);
            } else {
              // Get deals data
              const { data: dealsData, error: dealsError } = await proxyClient.get(`/api/borrower-data/deals/${dealNumber}`);
              
              if (dealsError) {
                console.error('Error fetching deal data for conditions:', dealsError);
              } else {
                console.log('All data fetched successfully, generating conditions...');
                
                // Generate conditions text
                const text = await generateConditionsText(
                  dealsData, 
                  borrowersData, 
                  incomesData, 
                  rentalPropertiesData
                );
                
                // Update state with generated text
                setConditionsText(text);
                
                // Save to database
                try {
                  const { error: saveError } = await proxyClient.post('/api/borrower-data/conditions', {
                    deal_number: dealNumber,
                    conditions_text: text
                  });
                  
                  if (saveError) {
                    console.error('Error saving conditions:', saveError);
                  } else {
                    console.log('Conditions generated and saved successfully');
                  }
                } catch (saveErr) {
                  console.error('Error saving generated conditions:', saveErr);
                }
              }
            }
          }
        }
      } catch (err) {
        console.error('Error generating conditions:', err);
      }
      
      setIsLoading(false);
    } catch (error: any) {
      console.error('Error pulling information:', error);
      setError(error.message || 'Failed to retrieve deal information');
      setIsLoading(false);
    }
  };

  const handleGenerateEnhancedDownpaymentReasonability = async () => {
    if (!downpaymentReasonabilityOriginal) return; // Check the correct state variable
    setDownpaymentReasonabilityGenerating(true);
    setDownpaymentReasonabilityError(null);
    try {
      // Use the correct state variable as input for the enhance function
      const enhanced = await enhanceDownpaymentReasonability(downpaymentReasonabilityOriginal); 
      setDownpaymentReasonabilityEnhanced(enhanced);
    } catch (err) { 
      console.error('Error in handleGenerateEnhancedDownpaymentReasonability:', err); // Added console log for the catch block
      setDownpaymentReasonabilityError("Failed to generate enhanced notes.");
    } finally {
      setDownpaymentReasonabilityGenerating(false);
    }
  }; 
  // --- End Downpayment Reasonability Handlers ---

  // Add missing handler functions for clipboard functionality
  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setCopySnackbarOpen(true);
      })
      .catch(err => {
        console.error('Could not copy text: ', err);
      });
  };

  // Add missing handler functions for edit mode
  const toggleEditMode = (key: string) => {
    setEditMode(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Add missing handlers for summary text
  const handleSummaryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSummaryText(e.target.value);
  };

  const handleEditDealSummary = () => {
    setOriginalSummaryText(summaryText); // Store original for cancel
    toggleEditMode('dealSummary');
  };

  const handleCancelDealSummaryEdit = () => {
    setSummaryText(originalSummaryText); // Restore original
    toggleEditMode('dealSummary');
  };

  // Add missing handlers for preliminary checks
  const handleEditPrelimCheck = () => {
    setOriginalPrelimCheckText(prelimCheckText);
    toggleEditMode('prelimCheck');
  };

  const handlePrelimCheckChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPrelimCheckText(e.target.value);
  };

  const handleSavePrelimCheck = async () => {
    if (!dealNumber || !prelimCheckText) return;
    
    setPrelimCheckSaveStatus('saving');
    try {
      // Use proxyClient instead of direct fetch
      const { error } = await proxyClient.post('/api/borrower-data/preliminary-checks', {
        dealNumber,
        checkText: prelimCheckText
      });
      
      if (error) {
        throw new Error('Failed to save preliminary checks: ' + error);
      }
      
      setPrelimCheckSaveStatus('saved');
      toggleEditMode('prelimCheck');
      
      // Reset status after 3 seconds
      setTimeout(() => setPrelimCheckSaveStatus('idle'), 3000);
    } catch (err) {
      console.error('Error saving preliminary checks:', err);
      setPrelimCheckSaveStatus('error');
      setPrelimCheckError('Failed to save preliminary checks. Please try again.');
      
      // Reset status after 3 seconds
      setTimeout(() => setPrelimCheckSaveStatus('idle'), 3000);
    }
  };

  const handleCancelPrelimCheckEdit = () => {
    setPrelimCheckText(originalPrelimCheckText);
    toggleEditMode('prelimCheck');
  };

  // Add missing handlers for IR summaries
  const handleSaveIRSummary = async (borrowerKey: string) => {
    const summary = irSummaries[borrowerKey];
    if (!dealNumber || !summary) return;
    
    // Save to backend API
    const borrowerNumber = parseInt(borrowerKey.replace('borrower', ''));
    const saved = await saveIRSummary(
      borrowerNumber, 
      summary.originalSummary, 
      summary.enhancedSummary
    );
    
    if (saved) {
      toggleEditMode(`${borrowerKey}-ir`);
    }
  };

  // Add missing handlers for rental reasonability
  const handleRentalReasonabilityChange = (type: 'original' | 'enhanced') => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (type === 'original') {
      setRentalReasonabilityOriginal(e.target.value);
    } else {
      setRentalReasonabilityEnhanced(e.target.value);
    }
  };

  const handleEditRentalReasonability = () => {
    setOriginalRentalReasonability(rentalReasonabilityOriginal);
    toggleEditMode('rentalReasonability');
  };

  const handleSaveRentalReasonability = async () => {
    if (!dealNumber) return;
    
    setRentalReasonabilitySaveStatus('saving');
    try {
      // Use proxyClient instead of direct fetch
      const { error } = await proxyClient.post('/api/borrower-data/rental-reasonability', {
        dealNumber,
        originalText: rentalReasonabilityOriginal,
        enhancedText: rentalReasonabilityEnhanced
      });
      
      if (error) {
        throw new Error('Failed to save rental reasonability: ' + error);
      }
      
      setRentalReasonabilitySaveStatus('saved');
      toggleEditMode('rentalReasonability');
      
      // Reset status after 3 seconds
      setTimeout(() => setRentalReasonabilitySaveStatus('idle'), 3000);
    } catch (err) {
      console.error('Error saving rental reasonability:', err);
      setRentalReasonabilitySaveStatus('error');
      setRentalReasonabilityError('Failed to save rental reasonability. Please try again.');
      
      // Reset status after 3 seconds
      setTimeout(() => setRentalReasonabilitySaveStatus('idle'), 3000);
    }
  };

  const handleCancelRentalReasonabilityEdit = () => {
    setRentalReasonabilityOriginal(originalRentalReasonability);
    toggleEditMode('rentalReasonability');
  };

  const handleGenerateEnhancedRentalReasonability = async () => {
    if (!rentalReasonabilityOriginal) return;
    
    setRentalReasonabilityGenerating(true);
    setRentalReasonabilityError(null);
    try {
      // Use proxyClient instead of direct fetch
      const { data, error } = await proxyClient.post('/api/borrower-data/openai', {
        prompt: `Please rewrite the following rental reasonability to be more concise and professional while keeping all the important details: ${rentalReasonabilityOriginal}`
      });
      
      if (error) {
        throw new Error('Failed to generate enhanced text: ' + error);
      }
      
      setRentalReasonabilityEnhanced(data.text);
      
      // Save the enhanced text
      await handleSaveRentalReasonability();
    } catch (err) {
      console.error('Error generating enhanced rental reasonability:', err);
      setRentalReasonabilityError('Failed to generate enhanced text. Please try again.');
    } finally {
      setRentalReasonabilityGenerating(false);
    }
  };

  // Add missing handlers for downpayment reasonability
  const handleDownpaymentReasonabilityChange = (type: 'original' | 'enhanced') => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (type === 'original') {
      setDownpaymentReasonabilityOriginal(e.target.value);
    } else {
      setDownpaymentReasonabilityEnhanced(e.target.value);
    }
  };

  const handleEditDownpaymentReasonability = () => {
    setOriginalDownpaymentReasonability(downpaymentReasonabilityOriginal);
    toggleEditMode('downpaymentReasonability');
  };

  const handleSaveDownpaymentReasonability = async () => {
    if (!dealNumber) return;
    
    setDownpaymentReasonabilitySaveStatus('saving');
    try {
      // Use proxyClient instead of direct fetch
      const { error } = await proxyClient.post('/api/borrower-data/downpayment-reasonability', {
        dealNumber,
        originalText: downpaymentReasonabilityOriginal,
        enhancedText: downpaymentReasonabilityEnhanced
      });
      
      if (error) {
        throw new Error('Failed to save downpayment reasonability: ' + error);
      }
      
      setDownpaymentReasonabilitySaveStatus('saved');
      toggleEditMode('downpaymentReasonability');
      
      // Reset status after 3 seconds
      setTimeout(() => setDownpaymentReasonabilitySaveStatus('idle'), 3000);
    } catch (err) {
      console.error('Error saving downpayment reasonability:', err);
      setDownpaymentReasonabilitySaveStatus('error');
      setDownpaymentReasonabilityError('Failed to save downpayment reasonability. Please try again.');
      
      // Reset status after 3 seconds
      setTimeout(() => setDownpaymentReasonabilitySaveStatus('idle'), 3000);
    }
  };

  const handleCancelDownpaymentReasonabilityEdit = () => {
    setDownpaymentReasonabilityOriginal(originalDownpaymentReasonability);
    toggleEditMode('downpaymentReasonability');
  };

  const enhanceDownpaymentReasonability = async (originalText: string): Promise<string> => {
    // Use proxyClient instead of direct fetch
    const { data, error } = await proxyClient.post('/api/borrower-data/openai', {
      prompt: `Please review and enhance the following downpayment reasonability notes while maintaining all the factual information, but making it more professionally worded and concise. Keep the same 'DP/R:' prefix and maintain all the specific details about sources, property references, and financial assessments: ${originalText}`
    });
    
    if (error) {
      throw new Error('Failed to generate enhanced text: ' + error);
    }
    
    return data.text;
  };

  // Add missing handler for CB notes
  const handleGenerateEnhancedCBNotes = async (borrowerKey: string) => {
    const noteData = borrowersNotes[borrowerKey];
    if (!noteData?.originalNotes) return;
    
    // Update state to show loading
    setBorrowersNotes(prev => ({
      ...prev,
      [borrowerKey]: {
        ...prev[borrowerKey],
        isSaving: true
      }
    }));
    
    try {
      // Call OpenAI to enhance the notes
      const enhancedNotes = await enhanceCBNotes(noteData.originalNotes);
      
      // Update state with enhanced notes
      setBorrowersNotes(prev => ({
        ...prev,
        [borrowerKey]: {
          ...prev[borrowerKey],
          enhancedNotes,
          isSaving: false
        }
      }));
      
      // Save to Supabase
      const borrowerNumber = parseInt(borrowerKey.replace('borrower', ''));
      await saveBorrowerNotes(borrowerNumber, noteData.originalNotes, enhancedNotes);
      
    } catch (err) {
      console.error('Error generating enhanced CB notes:', err);
      
      // Reset saving state
      setBorrowersNotes(prev => ({
        ...prev,
        [borrowerKey]: {
          ...prev[borrowerKey],
          isSaving: false
        }
      }));
    }
  };

  const handleSaveEnhancedNotes = async (borrowerKey: string) => {
    const noteData = borrowersNotes[borrowerKey];
    if (!dealNumber || !noteData) return;
    
    // Set saving state
    setBorrowersNotes(prev => ({
      ...prev,
      [borrowerKey]: {
        ...prev[borrowerKey],
        isSaving: true
      }
    }));
    
    try {
      // Save to backend
      const borrowerNumber = parseInt(borrowerKey.replace('borrower', ''));
      const saved = await saveBorrowerNotes(
        borrowerNumber, 
        noteData.originalNotes, 
        noteData.enhancedNotes
      );
      
      // Update state based on result
      if (saved) {
        toggleEditMode(borrowerKey);
      }
      
      // Reset saving state
      setBorrowersNotes(prev => ({
        ...prev,
        [borrowerKey]: {
          ...prev[borrowerKey],
          isSaving: false
        }
      }));
    } catch (err) {
      console.error('Error saving enhanced notes:', err);
      
      // Reset saving state
      setBorrowersNotes(prev => ({
        ...prev,
        [borrowerKey]: {
          ...prev[borrowerKey],
          isSaving: false
        }
      }));
    }
  };

  // Function to generate Downpayment Reasonability text based on source
  const generateDownpaymentReasonabilityText = (dealInfo: any): string => {
    console.log('Generating downpayment reasonability text with dealInfo:', dealInfo);
    console.log('Downpayment source:', dealInfo?.downpayment_source);
    
    // If downpayment_source field is undefined or N/A, don't print anything
    if (!dealInfo?.downpayment_source || dealInfo.downpayment_source === 'N/A') {
      console.log('No downpayment source or set to N/A, returning empty string');
      return '';
    }

    // Replace placeholders with actual values - remove parentheses
    const giftorValue = dealInfo?.giftor || '[Giftor Name]';
    
    // Determine rental property value
    // Check if we have the rental_property_address from the API call
    let rentalPropertyValue = dealInfo?.rental_property_address || '[Rental Property]';
    console.log('Using rental property address for template:', rentalPropertyValue);
    
    const deceasedValue = dealInfo?.deceased_name || '[Deceased Name]';
    const additionalNotes = dealInfo?.downpayment_source_notes ? 
      ` ${dealInfo.downpayment_source_notes}` : '';
    
    console.log('Values for template:', {
      giftorValue,
      rentalPropertyValue,
      deceasedValue,
      additionalNotes,
      exactSource: dealInfo.downpayment_source
    });

    // Generate text based on downpayment_source
    const source = dealInfo.downpayment_source.trim();
    console.log(`Using downpayment source: "${source}" for switch statement`);
    
    let result = '';
    
    switch (source) {
      case 'savings':
        console.log('Matched case: savings');
        result = `DP/R: DP will come from savings. B has sufficient liquid assets to cover DP. Accumulated amount deemed reasonable given clients' age, long tenure and employment as well as life stage.${additionalNotes} Minimal expenses / Credit and debt management in line with borrower's profile.`;
        break;
      
      case 'saleProceedsOO':
        console.log('Matched case: saleProceedsOO');
        result = `DP/R: DP will come from the sale of the owner-occupied property ${rentalPropertyValue}. B will have sufficient liquid assets to cover DP + fees. Proceeds supported by sale price & accumulated equity is reasonable given property value and ownership history.${additionalNotes} No concerns noted.`;
        break;
      
      case 'saleProceedsRental':
        console.log('Matched case: saleProceedsRental');
        result = `DP/R: DP will come from the sale of a rental property ${rentalPropertyValue}. B will have sufficient liquid assets to cover DP + fees. Proceeds in line with market value and consistent with client's investment strategy and risk profile. Long-term ownership, consistent rental income, and market appreciation support equity accumulation/value.${additionalNotes}`;
        break;
      
      case 'savingsGifted':
        console.log('Matched case: savingsGifted');
        result = `DP/R: DP will be sourced from personal savings and a non-repayable gift from ${giftorValue}. B has sufficient liquid assets to cover DP and related costs. Savings are reasonable based on age, stable employment, and life stage. Gifted funds from ${giftorValue} are also reasonable, reflecting the donor's accumulated wealth and intent to assist (recipient) with the subject property purchase.${additionalNotes} Combined sources align with borrower's financial profile.`;
        break;
      
      case 'savingsSaleProceeds':
        console.log('Matched case: savingsSaleProceeds');
        result = `DP/R: DP will be sourced from savings and sale proceeds of ${rentalPropertyValue}. B has sufficient liquid assets to cover both DP and closing costs. Equity and savings are reasonable given the client's early real estate investments, long-term property ownership, stable employment, and life stage. Asset accumulation aligns with borrower's financial behavior and long-term savings strategy.${additionalNotes}`;
        break;
      
      case 'saleProceedsGifted':
        console.log('Matched case: saleProceedsGifted');
        result = `DP/R: DP will be sourced from the sale of ${rentalPropertyValue} and gifted funds from ${giftorValue}. B will have sufficient liquid assets to cover DP and closing costs. Equity accumulation is reasonable given early investment in real estate, long-term ownership, and stable employment history. Gifted funds from ${giftorValue} are reasonable, reflecting donor's accumulated wealth and intent to assist (recipient) in acquiring the subject property.${additionalNotes} Combined sources align with client's life stage and financial profile.`;
        break;
      
      case 'inheritance':
        console.log('Matched case: inheritance');
        result = `DP/R: DP from inheritance after ${deceasedValue} death. Source verified via estate documents or legal confirmation. Funds align with client's asset profile and timing of estate distribution. Supporting documentation on file.${additionalNotes}`;
        break;
      
      default:
        console.log('No matching case found, using default case with source:', source);
        result = `DP/R: Source is ${dealInfo.downpayment_source}.${additionalNotes}`;
    }
    
    console.log('Generated downpayment text:', result);
    return result;
  };

  // Function to generate rental worksheet data
  const generateRentalWorksheetData = (): string => {
    if (!rentalProperties || rentalProperties.length === 0) {
      return "No rental properties found for this deal.";
    }

    // Create column prefixes based on property count (C5, D5, E5, F5, etc.)
    const columnPrefixes = rentalProperties.map((_, index) => {
      // Start with C (67 in ASCII) and increment for each property
      return String.fromCharCode(67 + index);
    });

    let result = "";

    // Add deal_number section
    result += "DEAL NUMBERS:\n";
    // C2, D2, E2, F2 → Deal Number values
    result += columnPrefixes.map((prefix, index) => 
      `${prefix}2: ${rentalProperties[index].deal_number || dealNumber || "Deal number not found"}`
    ).join("\n") + "\n\n";

    // Add section headers
    result += "PROPERTY ADDRESSES:\n";
    // C5, D5, E5, F5 → Address values
    result += columnPrefixes.map((prefix, index) => 
      `${prefix}5: ${rentalProperties[index].address || "Address not found"}`
    ).join("\n") + "\n\n";
    
    result += "RENTAL INCOME:\n";
    // C6, D6, E6, F6 → Rental Income values
    result += columnPrefixes.map((prefix, index) => 
      `${prefix}6: ${rentalProperties[index].rental_income || "0"}`
    ).join("\n") + "\n\n";
    
    result += "MORTGAGE PAYMENTS:\n";
    // C10, D10, E10, F10 → Mortgage Payment values
    result += columnPrefixes.map((prefix, index) => 
      `${prefix}10: ${rentalProperties[index].mortgage_payment || "0"}`
    ).join("\n") + "\n\n";
    
    result += "HEAT COSTS:\n";
    // C11, D11, E11, F11 → Heat values
    result += columnPrefixes.map((prefix, index) => 
      `${prefix}11: ${rentalProperties[index].heat || "0"}`
    ).join("\n") + "\n\n";
    
    result += "PROPERTY TAX:\n";
    // C12, D12, E12, F12 → Property Tax values
    result += columnPrefixes.map((prefix, index) => 
      `${prefix}12: ${rentalProperties[index].property_tax || "0"}`
    ).join("\n") + "\n\n";
    
    result += "CONDO FEES:\n";
    // C13, D13, E13, F13 → Condo Fees values
    result += columnPrefixes.map((prefix, index) => 
      `${prefix}13: ${rentalProperties[index].condo_fees || "0"}`
    ).join("\n") + "\n\n";
    
    result += "TOTAL OWNERSHIP PERCENTAGE:\n";
    // C16, D16, E16, F16 → Total Percentage values
    result += columnPrefixes.map((prefix, index) => {
      const property = rentalProperties[index];
      const b1 = parseFloat(property.owned_percent_b1 || "0");
      const b2 = parseFloat(property.owned_percent_b2 || "0");
      const b3 = parseFloat(property.owned_percent_b3 || "0");
      const b4 = parseFloat(property.owned_percent_b4 || "0");
      const totalPercent = (b1 + b2 + b3 + b4).toFixed(2);
      return `${prefix}16: ${totalPercent}`;
    }).join("\n");

    return result;
  };

  // Function to generate conditions text based on deal data
  const generateConditionsText = async (dealData: any, borrowersData: any, incomesData: any, rentalPropertiesData: any) => {
    if (!dealData || !borrowersData || (dealData.length === 0) || (borrowersData.length === 0)) {
      return 'Unable to generate conditions text: missing deal or borrower data.';
    }

    const deal = dealData[0];
    let conditionsText = '';

    // Helper function to add a condition with proper spacing
    const addCondition = (condition: string) => {
      conditionsText += condition + '\n\n';
    };

    // ALL DEALS SECTION
    addCondition('All Deals');
    addCondition('Solicitor to provide commitment to Title Insurance prior to funding. If applicable, condo, water, and septic system endorsements are also required.');
    
    addCondition('Solicitor to confirm that the applicant(s) do not own any other properties other than the one(s) disclosed to First National.');

    // Get subject property tax value
    const subjectProperties = rentalPropertiesData.filter((prop: any) => 
      ['Subject O/O', 'Subject rental', 'Subject 2nd Home', 'Subject O/O + Rental'].includes(prop.property_type)
    );

    let propertyTaxValue = '';
    
    if (subjectProperties.length > 0) {
      const subjectProperty = subjectProperties[0];
      
      if ((!subjectProperty.calculated_tax_plus_3_percent || subjectProperty.calculated_tax_plus_3_percent === '0.00') && 
          (!subjectProperty.calculated_one_percent_pp || subjectProperty.calculated_one_percent_pp === '0.00')) {
        propertyTaxValue = subjectProperty.property_tax || '';
      } else if (subjectProperty.calculated_tax_plus_3_percent && subjectProperty.calculated_tax_plus_3_percent !== '0.00') {
        propertyTaxValue = subjectProperty.calculated_tax_plus_3_percent;
      } else if (subjectProperty.calculated_one_percent_pp && subjectProperty.calculated_one_percent_pp !== '0') {
        propertyTaxValue = subjectProperty.calculated_one_percent_pp;
      }
    }

    // Property Taxes condition based on deal type
    if (deal.deal_type === 'PUR') {
      addCondition(`Solicitor to confirm that annual property taxes for the subject property do not exceed $${propertyTaxValue} and are paid up to date on closing.`);
    } else if (deal.deal_type === 'Refi') {
      addCondition(`Solicitor to confirm that annual property taxes for the subject property do not exceed $${propertyTaxValue} and are paid up to date on closing. If closing via FCT, Broker to provide the confirmation.`);
    }

    // Province-specific conditions
    if (deal.province === 'BC') {
      addCondition('Broker to provide copy of executed BC disclosure statement and Form 10.');
    } else if (['NS', 'SK', 'AB', 'MB'].includes(deal.province)) {
      addCondition('Solicitor to provide a satisfactory and fully signed disclosure to borrower.');
    }

    // SIN/Citizenship conditions
    const borrowersNeedingCitizenship = borrowersData.filter((borrower: any) => 
      borrower.has_sin === 'no' || borrower.sin_cb_status === 'Not reporting' || borrower.sin_cb_status === 'Yes'
    );
    
    if (borrowersNeedingCitizenship.length > 0) {
      const borrowerNames = borrowersNeedingCitizenship.map((borrower: any) => borrower.name).join(', ');
      addCondition(`Solicitor to provide proof of Canadian citizenship status via Canadian birth certificate, passport, citizenship card, or permanent resident card for ${borrowerNames}`);
    }

    // DOB Merlin mismatch
    const borrowersWithDobMismatch = borrowersData.filter((borrower: any) => 
      borrower.dob && borrower.dob_merlin && borrower.dob !== borrower.dob_merlin
    );
    
    if (borrowersWithDobMismatch.length > 0) {
      const borrowerNames = borrowersWithDobMismatch.map((borrower: any) => borrower.name).join(', ');
      addCondition(`Broker to provide a copy of ID to confirm the date of birth for: ${borrowerNames}`);
    }

    // Pre-app condition
    if (deal.deal_type === 'Pre-app') {
      addCondition("Final approval is subject to meeting First National's lending standards for property eligibility, credit history, employment income and down payment verification 120 days before closing.");
    }

    // INCOME SECTION
    addCondition('INCOME');

    // Process each income
    if (incomesData && incomesData.length > 0) {
      for (const income of incomesData) {
        const borrower = borrowersData.find((b: any) => b.id === income.borrower_id);
        if (!borrower) continue;
        
        const borrowerName = borrower.name;
        const annualIncome = income.annual_income || '0';

        // Apply income type conditions
        switch(income.income_description) {
          case 'Full Time':
            addCondition(`Broker to provide recent employment letter dated within 60 days and current paystub within 30 days confirming ${borrowerName} is a full-time permanent employee with an annual salary of $${annualIncome}.`);
            break;
          case 'Part Time':
            addCondition(`Broker to provide recent employment letter dated within 60 days and current paystub with 30 days confirming ${borrowerName} is a part-time employee earning ${annualIncome}. If hours are not guaranteed, broker to provide two years of T4s or Notice of Assessments.`);
            break;
          case 'Hourly':
            addCondition(`Broker to provide confirmation that ${borrowerName} is a permanent employee with guaranteed hours and an annual income of $${annualIncome} via recent employment letter dated within 60 days and current pay stub within 30 days.`);
            break;
          case 'Fluctuating':
            addCondition(`Broker to provide most recent 1) employment letter, 2) paystub, 3) two years of Notice of Assessments or T4s or Year-End Paystubs confirming ${borrowerName} is a permanent employee with an average annual income of $${annualIncome}.`);
            break;
          case 'Seasonal':
            addCondition(`Broker to provide most recent two years of T4E's to confirm seasonal EI income of $${annualIncome} for ${borrowerName}.`);
            break;
          case 'BFS':
            addCondition(`Broker to provide the following business for self income confirmation for ${borrowerName}:
- Signed First National self-declared income letter. 
- 12 months' bank statements. 
*Bank statements must be supported by any of the following:
-	Invoices confirming at least 2 deposits.
-	Signed and executed contract(s).
-	Merchant statements confirming transactions. 
&
Solicitor to provide an affidavit or statutory declaration confirming income taxes are current with no outstanding balance for ${borrowerName}.`);
            break;
          // ... More income types would continue here
          // For brevity I've shown just the first few types as examples
        }
      }
    }

    // Process child support fields for all borrowers
    borrowersData.forEach((borrower: any) => {
      if (borrower.child_support === true) {
        addCondition(`Broker to provide the executed Separation Agreement confirming support payments do not exceed $XX monthly.`);
      }
      
      if (borrower.no_child_support === true) {
        addCondition(`Broker to provide satisfactory separation agreement confirming ${borrower.name} is not required to pay child support or alimony and division of assets has been finalized.`);
      }
      
      if (borrower.stat_dec_for_separation === true) {
        addCondition(`Solicitor to provide statutory declaration confirming that ${borrower.name} does not have to pay any child support or alimony.`);
      }
    });

    // RENTALS SECTION
    addCondition('RENTALS');
    
    // Process each rental property
    if (rentalPropertiesData && rentalPropertiesData.length > 0) {
      for (const property of rentalPropertiesData) {
        // Example of one rental condition (more would follow in the full implementation)
        if (['Subject Rental', 'Subject O/O + Rental'].includes(property.property_type) && property.lease_agreement === true) {
          addCondition(`Broker to provide a satisfactory copy of the current rental agreement for the subject property confirming annual income of $${property.rental_income}.
If the lease has expired/is monthly, then a tenant acknowledgment will also be required.`);
        }
        
        if (['Subject Rental', 'Subject O/O + Rental'].includes(property.property_type) && property.market_rent === true) {
          addCondition(`Broker to request a market rent analysis on subject property by an approved appraiser, confirming market rents of at least $${property.rental_income}/year.`);
        }
        
        if (property.property_type === 'Subject O/O + Rental' && property.market_rent === true) {
          addCondition(`Solicitor to provide statutory declaration confirming that the subject property will be tenanted and owner-occupied.`);
        }
        
        // Rental property conditions with lease agreement
        if (property.property_type === 'Rental' && property.lease_agreement === true) {
          let taxValue = '';
          if ((!property.calculated_tax_plus_3_percent || property.calculated_tax_plus_3_percent === '0.00') && 
              (!property.calculated_one_percent_pp || property.calculated_one_percent_pp === '0.00')) {
            taxValue = property.property_tax || '';
          } else if (property.calculated_tax_plus_3_percent && 
                    (property.calculated_one_percent_pp === '0.00' || !property.calculated_one_percent_pp)) {
            taxValue = property.calculated_tax_plus_3_percent;
          } else if (property.calculated_one_percent_pp && property.calculated_one_percent_pp !== '0') {
            taxValue = property.calculated_one_percent_pp;
          }
          
          let conditionText = `Broker to provide the following for the rental property at ${property.address}:
- Current lease agreement(s) confirming annual rental income of $${property.rental_income} from XX units.
- Tax bill confirming annual taxes do not exceed $${taxValue}.`;

          if (property.property_type === 'condominium') {
            conditionText += `\n-- If the property is a condominium, confirmation of condo fees must be provided.`;
          }
          
          // Add mortgage info if applicable
          if (property.mortgage_amount && property.mortgage_amount !== '0' && property.out_of_province === false) {
            // Find owner names based on owned_percent_b1, owned_percent_b2, owned_percent_b3, owned_percent_b4
            const ownerNames: string[] = [];
            for (let i = 1; i <= 4; i++) {
              const percent = property[`owned_percent_b${i}`];
              if (percent && Number(percent) > 0) {
                const borrower = borrowersData.find((b: any) => Number(b.borrower_number) === i);
                if (borrower && borrower.name) {
                  ownerNames.push(borrower.name);
                }
              }
            }
            const owners = ownerNames.join(' & ');
            conditionText += `\n- Most recent mortgage information statement confirming mortgage of $${property.mortgage_amount} with monthly payments not exceeding $${property.mortgage_payment} and that it is in good standing. Statement to confirm ${owners} is/are a 100% owner(s).`;
          }
          
          // Add out of province condition if applicable
          if (property.mortgage_amount && property.mortgage_amount !== '0' && property.out_of_province === true) {
            conditionText += `\n- Broker to provide title/PIN search for ${property.address} due to being located out of province. Title search must confirm the only registered encumbrance is a first mortgage.`;
          }
          
          if ((!property.mortgage_amount || property.mortgage_amount === '0') && property.out_of_province === true) {
            conditionText += `\nBroker to provide title/PIN search for ${property.address} confirming property is Free & Clear if any liens or encumbrances, due to being located out of province.`;
          }
          
          // Add solicitor conditions
          if (property.mortgage_amount && property.mortgage_amount !== '0' && property.out_of_province === false) {
            conditionText += `\n&\nSolicitor to provide confirmation the only encumbrance registered to ${property.address} is a first mortgage.`;
          }
          
          if ((!property.mortgage_amount || property.mortgage_amount === '0') && property.out_of_province === false) {
            conditionText += `\n&\nSolicitor to confirm that the property located at - ${property.address} is free and clear of any liens or encumbrances.`;
          }
          
          addCondition(conditionText);
        }
        
        // Notice to vacate
        if (property.notice_to_vacate === true) {
          addCondition(`Broker to provide confirmation that the existing tenant has been given proper notice to vacate as per the Landlord Tenant Act for ${property.address}.`);
        }
      }
    }
    
    // DOWNPAYMENT SECTION
    addCondition('DOWNPAYMENT');
    
    // Default downpayment condition for all deals
    addCondition(`Broker to provide 30 days bank statements showing down payment, deposit and 1.5% closing cost. Bank statement should clearly show clients name, current address, account number and financial institution. Any large deposits must be sourced.`);
    
    // Source-specific downpayment conditions
    if (deal.downpayment_sources) {
      switch(deal.downpayment_sources) {
        case 'HELOC/Refinance':
          addCondition(`Broker to provide current HELOC/mortgage commitment statement showing clients name, address, rate and financial institution. Statement/commitment to show available balance required for down payment and 1.5% closing cost. Trust ledger to be provided.`);
          break;
        case 'Gifted':
        case 'Sale proceeds + Gifted':
          addCondition(`Broker to provide satisfactory confirmation of gifted funds in the amount of $${deal.downpayment_amount} on deposit in the borrower's bank account.`);
          break;
        case 'Gifted/Donor\'s Possession':
          addCondition(`Broker to provide donor's bank statement confirming sufficient funds available to provide the gifted down payment.`);
          break;
        case 'Gifted/Lawyer':
          addCondition(`Solicitor to provide statement of adjustments or trust ledger confirming receipt of gifted funds from ${deal.giftor}. Solicitor must confirm name of person providing the funds.`);
          break;
        case 'Gifted Equity':
          const borrowerNames = borrowersData.map((b: any) => b.name).join(', ');
          addCondition(`Broker to provide satisfactory gift letter using First National template confirming donor is gifting ${borrowerNames} existing equity from (subject property address) in the amount of $${deal.downpayment_amount}`);
          break;
        case 'Inheritance':
          addCondition(`Broker to provide confirmation of inherited funds through a copy of the will or a letter from the solicitor. The letter must outline the amount and date when the inheritance was received. A bank statement is also required to confirm the funds were received and available.`);
          break;
        case 'Sale Proceeds of O/O':
        case 'Sale proceeds of Rental Property':
        case 'Savings + Sale proceeds':
        case 'Sale proceeds + Gifted':
          // Properties being sold for downpayment
          const beingSoldProperties = rentalPropertiesData.filter((p: any) => p.being_sold_for_dp === true);
          if (beingSoldProperties.length > 0) {
            const addresses = beingSoldProperties.map((p: any) => p.address).join(', ');
            const mortgageAmounts = beingSoldProperties.map((p: any) => p.mortgage_amount).join(', ');
            
            addCondition(`Broker to provide copy of firm purchase and sale agreement along with all waivers, and amendments for the property being sold for down payment: ${addresses}
Solicitor to confirm that the only encumbrance registered to property at ${addresses} is a mortgage of ${mortgageAmounts} and is up to date and in good standing. Discharge Statement(s) to be provided.
Solicitor to provide a copy of the trust ledger from the sale of property ${addresses} confirming sufficient funds for down payment, closing costs of 1.5% of the purchase price and to payout mortgage of ${mortgageAmounts}. Solicitor to payout these debts.`);
          }
          break;
        case 'Cryptocurrency':
          addCondition(`Broker to provide statements with ownership information confirming all cryptocurrencies held, along with fund continuity documents to confirm sale of cryptocurrency in a crypto wallet owned by the borrower.`);
          break;
      }
    }
    
    // BRIDGE LOAN SECTION
    if (deal.bridge === true) {
      addCondition('BRIDGE LOAN');
      
      // Properties being sold for downpayment (for bridge loan address)
      const beingSoldProperties = rentalPropertiesData.filter((p: any) => p.being_sold_for_dp === true);
      const bridgeAddress = beingSoldProperties.length > 0 ? beingSoldProperties[0].address : '';
      
      if (deal.province === 'ON') {
        addCondition(`Bridge financing to be arranged prior to funding at a rate of Prime + 2% and a fee of $500.
Broker to provide mortgage statement for the property located at ${bridgeAddress} to confirm existing mortgage. 
IF BRIDGE IS $300K+ OR EXCEEDS 30 DAYS:
Solicitor to register a mortgage for interim financing on ${bridgeAddress}
OR IF BRIDGE IS UNDER AND UNDER $300K:
Solicitor to provide undertaking that if the sale transaction does not close as scheduled, the solicitor will register the interim financing on ${bridgeAddress}.`);
      } else if (['BC', 'AB', 'SK', 'MB'].includes(deal.province)) {
        addCondition(`Bridge financing to be arranged prior to funding at a rate of Prime + 2% and a fee of $500.
Broker to provide mortgage statement for the property located at ${bridgeAddress} to confirm existing mortgage. 
IF BRIDGE IS $300K+ OR EXCEEDS 30 DAYS:
Solicitor to register a mortgage for interim financing on ${bridgeAddress}.
IF BRIDGE IS UNDER $300K AND UNDER 30 DAYS): 
Solicitor to provide undertaking that if the sale transaction does not close as scheduled, the solicitor will register a mortgage on ${bridgeAddress} for the bridged amount.`);
      }
    }
    
    // PURCHASES SECTION
    if (deal.deal_type === 'PUR') {
      addCondition('PURCHASES');
      
      if (deal.deal_type === 'Private PUR ON') {
        addCondition(`Private Purchase – Ontario 
Solicitor to submit the statement of adjustments confirming purchase price and deposit on offer; transfer of deed; and obtain title insurance. 
Receipt of satisfactory purchase agreement including all addendums prepared by a solicitor. If not prepared by solicitor, purchase agreement including all addendums, is subject to satisfactory review by the solicitor prior to funding.`);
      } else if (deal.deal_type === 'Private PUR BC') {
        addCondition(`Private Purchase – BC
Broker to provide satisfactory private purchase agreement including all addendums, subject removals, PDS, and confirmation of current year gross property taxes.
Solicitor to confirm title has not been transferred within the last 12 months. 
Solicitor to provide statement of adjustment confirming transaction, purchase price, and deposit held in trust. Solicitor to provide title insurance on subject property.`);
      } else if (deal.deal_type === 'Private PUR AB') {
        addCondition(`Private Purchase – Alberta 
Broker to provide fully executed purchase agreement, confirmation of deposit held with solicitor, current copy of title, and confirmation of current year tax levy.:
Solicitor to provide statement of adjustment confirming transaction, purchase price, and deposit held in trust. Solicitor to provide title insurance on subject property.`);
      }
      
      // Check for new builds
      const newBuiltProperties = rentalPropertiesData.filter((p: any) => p.new_built === true);
      if (newBuiltProperties.length > 0) {
        addCondition(`Solicitor to provide a copy of New Home Warranty Certificate and Occupancy Certificate. 
Solicitor to submit the final statement of adjustments confirming the purchase price and deposit on the offer.
Broker to provide full purchase and sale agreement with all signed amendments and all copies of deposit cheques for the subject property.`);
      }
    }
    
    // REFINANCES SECTION
    if (deal.deal_type === 'REFI') {
      addCondition('REFINANCES');
      
      // Get subject property addresses
      const subjectAddresses = rentalPropertiesData
        .filter((p: any) => ['Subject O/O', 'Subject O/O + Rental', 'Subject Rental'].includes(p.property_type))
        .map((p: any) => p.address)
        .join(', ');
        
      addCondition(`Mortgage/debt payout from proceeds: *use template condition in Merlin*
Solicitor to confirm that the only encumbrance registered at the subject property is ${subjectAddresses}. Discharge statement to be provided to confirm mortgage is up to date and in good standing.`);
    }
    
    // DEBTS & MORTGAGES SECTION
    let debtsMortgagesAdded = false;
    
    // Check for consumer proposals, bankruptcies
    for (const borrower of borrowersData) {
      if (borrower.cp_active === true) {
        if (!debtsMortgagesAdded) {
          addCondition('DEBTS & MORTGAGES');
          debtsMortgagesAdded = true;
        }
        addCondition(`Active Consumer Proposal
Broker to provide copy of consumer proposal and letter from trustee for ${borrower.name} to show current balance and that it has been paid as agreed.`);
      }
      
      if (borrower.BKRPT === true) {
        if (!debtsMortgagesAdded) {
          addCondition('DEBTS & MORTGAGES');
          debtsMortgagesAdded = true;
        }
        addCondition(`Broker to provide a copy of the full bankruptcy package and certificate of discharge.`);
      }
      
      if (borrower.cp_discharged === true) {
        if (!debtsMortgagesAdded) {
          addCondition('DEBTS & MORTGAGES');
          debtsMortgagesAdded = true;
        }
        addCondition(`Broker to provide a copy of the full consumer proposal package and certificate of discharge`);
      }
    }
    
    // CONDOMINIUMS SECTION
    const condoProperties = rentalPropertiesData.filter((p: any) => p.condo_fees && p.condo_fees !== '0');
    if (condoProperties.length > 0) {
      addCondition('CONDOMINIUMS');
      addCondition(`Solicitor to provide confirmation of condo fees for the subject property.`);
      // Note: Commented conditions that need confirmation
      // addCondition(`Solicitor to provide latest audited condominium financial statements and certificate of insurance satisfactory to First National.`);
      // addCondition(`Broker to confirm subject property was not built using post tension cable construction.`);
    }
    
    // REMOVING OFF TITLE SECTION
    if (deal.deal_conditions && deal.deal_conditions.remove_from_title === true) {
      addCondition('REMOVING OFF TITLE');
      addCondition(`Solicitor to remove (name) off title to the subject property.`);
    }
    
    // AKA SECTION
    const akaApplicants = borrowersData.filter(b => b.aka === 'Yes');
    if (akaApplicants.length > 0) {
      addCondition('AKA');
      for (const borrower of akaApplicants) {
        addCondition(`Solicitor to provide signed statutory declaration confirming ${borrower.name} and ${borrower.aka_reason} are one and the same person.`);
      }
    }
    
    // TITLE CONFIRMATION SECTION
    const notOnTitleBorrowers = borrowersData.filter(b => b.not_on_title && b.not_on_title !== '0' && b.not_on_title !== null);
    if (notOnTitleBorrowers.length > 0) {
      addCondition('TITLE CONFIRMATION');
      for (const borrower of notOnTitleBorrowers) {
        addCondition(`Solicitor to confirm that ${borrower.name} is not on title to any of the following properties: ${borrower.not_on_title}.`);
        // Alternative version:
        // addCondition(`Solicitor to provide statutory declaration confirming that ${borrower.name} is only on title to the subject property and no other properties.`);
      }
    }
    
    // OTHER PROPERTIES OWNED SECTION
    const otherProperties = rentalPropertiesData.filter(p => p.property_type === 'O/O' || p.property_type === 'Second Home');
    if (otherProperties.length > 0) {
      addCondition('OTHER PROPERTIES OWNED');
      
      for (const property of otherProperties) {
        // Calculate tax value
        let taxValue = '';
        if ((!property.calculated_tax_plus_3_percent || property.calculated_tax_plus_3_percent === '0.00') && 
            (!property.calculated_one_percent_pp || property.calculated_one_percent_pp === '0.00')) {
          taxValue = property.property_tax || '';
        } else if (property.calculated_tax_plus_3_percent && 
                  (property.calculated_one_percent_pp === '0.00' || !property.calculated_one_percent_pp)) {
          taxValue = property.calculated_tax_plus_3_percent;
        } else if (property.calculated_one_percent_pp && property.calculated_one_percent_pp !== '0') {
          taxValue = property.calculated_one_percent_pp;
        }
        
        let conditionText = `Broker to provide the following for the property ${property.address}:
- Tax bill confirming annual taxes do not exceed $${taxValue}.`;
        
        // Add mortgage info if applicable
        if (property.mortgage_amount && property.mortgage_amount !== '0') {
          conditionText += `\n- Most recent mortgage information statement confirming mortgage of $${property.mortgage_amount} with monthly payments not exceeding $${property.mortgage_payment} and that it is in good standing.`;
        }
        
        // Add rental income if applicable
        if (property.rental_income) {
          conditionText += `\n- Current lease agreement(s) confirming annual rental income of $${property.rental_income} from XX units.`;
        }
        
        // Add condo info if applicable
        if (property.property_type === 'condominium') {
          conditionText += '\n- If the property is a condominium, provide confirmation of condo fees.';
        }
        
        // Add out of province/mortgage conditions
        if (property.mortgage_amount && property.mortgage_amount !== '0' && property.out_of_province === false) {
          // Get owner names
          const owners = borrowersData.filter((b: any) => 
            property.ownership && property.ownership[b.id] && property.ownership[b.id] > 0
          ).map((b: any) => b.name).join(', ');
          
          conditionText += `\n- Most recent mortgage information statement confirming mortgage of $${property.mortgage_amount} with monthly payments not exceeding $${property.mortgage_payment} and that it is in good standing. Statement to confirm ${owners} is/are a 100% owner(s).`;
        }
        
        if (property.mortgage_amount && property.mortgage_amount !== '0' && property.out_of_province === true) {
          conditionText += `\n- Broker to provide title/PIN search for ${property.address} due to being located out of province. Title search must confirm the only registered encumbrance is a first mortgage.`;
        }
        
        if ((!property.mortgage_amount || property.mortgage_amount === '0') && property.out_of_province === true) {
          conditionText += `\n- Broker to provide title/PIN search for ${property.address} confirming property is Free & Clear if any liens or encumbrances, due to being located out of province.`;
        }
        
        // Add solicitor conditions
        if (property.mortgage_amount && property.mortgage_amount !== '0' && property.out_of_province === false) {
          conditionText += `\n- Solicitor to provide confirmation the only encumbrance registered to ${property.address} is a first mortgage.`;
        }
        
        if ((!property.mortgage_amount || property.mortgage_amount === '0') && property.out_of_province === false) {
          conditionText += `\n- Solicitor to confirm that the property located at - ${property.address} is free and clear of any liens or encumbrances.`;
        }
        
        addCondition(conditionText);
      }
      
      // Special condition for O/O + Rental properties
      const ooRentalProperties = rentalPropertiesData.filter(p => p.property_type === 'O/O + Rental');
      if (ooRentalProperties.length > 0) {
        for (const property of ooRentalProperties) {
          let taxValue = '';
          if ((!property.calculated_tax_plus_3_percent || property.calculated_tax_plus_3_percent === '0.00') && 
              (!property.calculated_one_percent_pp || property.calculated_one_percent_pp === '0.00')) {
            taxValue = property.property_tax || '';
          } else if (property.calculated_tax_plus_3_percent && 
                    (property.calculated_one_percent_pp === '0.00' || !property.calculated_one_percent_pp)) {
            taxValue = property.calculated_tax_plus_3_percent;
          } else if (property.calculated_one_percent_pp && property.calculated_one_percent_pp !== '0') {
            taxValue = property.calculated_one_percent_pp;
          }
          
          let conditionText = `Broker to provide the following for the property ${property.address}:
- Tax bill confirming taxes do not exceed $${taxValue}/month.`;
          
          // Add mortgage info if applicable
          if (property.mortgage_amount && property.mortgage_amount !== '0') {
            conditionText += `\n- Most recent mortgage information statement confirming mortgage of $${property.mortgage_amount} with monthly payments not exceeding $${property.mortgage_payment} and that it is in good standing.`;
          }
          
          // Add condo info if applicable
          if (property.property_type === 'condominium') {
            conditionText += '\n- If the property is a condominium, provide confirmation of condo fees.';
          }
          
          // Same mortgage/out of province conditions as above
          if (property.mortgage_amount && property.mortgage_amount !== '0' && property.out_of_province === false) {
            // Get owner names
            const owners = borrowersData.filter((b: any) => 
              property.ownership && property.ownership[b.id] && property.ownership[b.id] > 0
            ).map((b: any) => b.name).join(', ');
            
            conditionText += `\n- Most recent mortgage information statement confirming mortgage of $${property.mortgage_amount} with monthly payments not exceeding $${property.mortgage_payment} and that it is in good standing. Statement to confirm ${owners} is/are a 100% owner(s).`;
          }
          
          // Add other conditions similar to above
          
          addCondition(conditionText);
        }
      }
    }
    
    // EXTENDED RATIOS PROGRAM SECTION
    if (deal.mtg_type === 'ERP') {
      addCondition('EXTENDED RATIOS PROGRAM');
      addCondition(`Appraisal to confirm subject has municipal water and sewers.
All documentation, including income verification, down payment details, and appraisal reports, must be submitted at least five days before the closing date.`);
    }
    
    // OTHER CONDITIONS SECTION
    addCondition('OTHER CONDITIONS');
    
    if (deal.mtg_type === 'NCL') {
      addCondition(`NCL - Mortgage amount not to exceed the lesser of $(Insert the MTG amount approved) or 65% LTV.`);
    }
    
    if (deal.deal_conditions && deal.deal_conditions.rate_discount_65 === true) {
      addCondition(`Rate is valid for conforming loans at 65% loan-to-value or less`);
    }
    
    if (deal.deal_conditions && deal.deal_conditions.rate_discount_50 === true) {
      addCondition(`Rate is valid for conforming loans at 50% loan-to-value or less.`);
    }
    
    const secondHomeProperties = rentalPropertiesData.filter(p => p.property_type === 'Second Home');
    if (secondHomeProperties.length > 0) {
      addCondition(`Solicitor to ensure a statutory declaration is signed by all borrowers stating subject property will be owner-occupied at least once during the year or occupied by a family member on a rent-free basis. No income is to be derived from this property.`);
    }
    
    // HOLDBACK SECTION
    if (deal.deal_conditions && deal.deal_conditions.hold_back === true) {
      addCondition('HOLDBACK');
      addCondition(`Solicitor to hold back the sum of $_____ pending completion of the improvements. Usual sub-search and Construction Lien Act requirements are to be adhered to at time of release of holdback.
Holdback to be released at First National's authorization and upon receipt of satisfactory confirmation the improvements have been completed and satisfactory confirmation from a home inspection that work is completed as per quote on file and proof of paid invoices and home inspection report from an approved First National Appraiser.
Broker to provide a quote for the improvements from a reputable (**enter type of tradesperson completing the work e.g., roofer, contractor**) prior to closing.`);
    }
    
    if (deal.deal_conditions && deal.deal_conditions.financial_mgm === true) {
      addCondition(`Broker to provide most recent 6 months' bank statements for (client's name) demonstrating financial management.`);
    }
    
    if (deal.deal_conditions && deal.deal_conditions.purchase_assignments === true) {
      addCondition(`Subject to receipt of a satisfactory assignment agreement, original purchase agreement including all addendums, and the builder's acknowledgement.`);
    }
    
    if (deal.deal_conditions && deal.deal_conditions.duplex_2_units === true) {
      addCondition(`Duplex Properties with 2 Unit Numbers Under One Title
Solicitor to provide written confirmation that the property located at *address* is under one title with one tax bill. Current year taxes are not to exceed *tax amount*/year for all units.`);
    }
    
    // Add temporary resident conditions if needed
    if (deal.deal_conditions && deal.deal_conditions.temporary_resident === true) {
      addCondition('TEMPORARY RESIDENT (ONE APPLICANT HAS 9-SIN)');
      addCondition(`Purchase – Work permit had 183 days or more remaining at time of purchase
Broker to provide the following for (*name of borrower*):
- Work permit that was valid for at least 183 days from the date of the purchase agreement signing. 
Solicitor to provide statutory declaration confirming (*name of borrower*) does not currently own any other property in Canada.`);
      // Additional temporary resident conditions would be added here
    }
    
    // PORTS SECTION
    if (deal.deal_conditions && deal.deal_conditions.ports === true) {
      addCondition('PORTS');
      addCondition(`Broker to provide copy of firm purchase and sale agreement along with all waivers, and amendments for the property being sold for down payment: 
Solicitor to confirm that the only encumbrance registered to property at (SALE ADDRESS) is mortgage of (MORTGAGE AMOUNT) and is up to date and in good standing. Discharge Statement(s) to be provided.
Solicitor to port the existing mortgage of $(*mortgage amount*) from (*address of sale property*) to the new purchase property (*address of new purchase property*)
The penalty charged on existing mortgage (###) will be calculated 15 days prior to the new mortgage closing. Provided the new mortgage closes with First National, the penalty will be refunded.`);
    }
    
    return conditionsText;
  };

  // Function to save conditions to the database
  const saveConditionsText = async () => {
    if (!dealNumber) return;
    
    try {
      setConditionsSaveStatus('saving');
      
      const { error } = await proxyClient.post('/api/borrower-data/conditions', {
        deal_number: dealNumber,
        conditions_text: conditionsText
      });
      
      if (error) {
        console.error('Error saving conditions:', error);
        setConditionsSaveStatus('error');
        setConditionsError('Failed to save conditions: ' + error.message);
        return false;
      }
      
      setConditionsSaveStatus('saved');
      setTimeout(() => setConditionsSaveStatus('idle'), 3000);
      return true;
    } catch (err: any) {
      console.error('Error saving conditions:', err);
      setConditionsSaveStatus('error');
      setConditionsError('Failed to save conditions: ' + err.message);
      return false;
    }
  };

  // Function to handle edit mode for conditions
  const handleEditConditions = () => {
    setOriginalConditionsText(conditionsText);
    setEditingConditions(true);
  };

  // Function to handle conditions text change
  const handleConditionsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConditionsText(e.target.value);
  };

  // Function to save edited conditions
  const handleSaveConditions = async () => {
    const success = await saveConditionsText();
    if (success) {
      setEditingConditions(false);
    }
  };

  // Function to cancel conditions editing
  const handleCancelConditionsEdit = () => {
    setConditionsText(originalConditionsText);
    setEditingConditions(false);
    setConditionsError(null);
  };

  // Function to generate conditions
  const handleGenerateConditions = async () => {
    if (!dealNumber || !dealInfo) {
      setConditionsError('Deal information is required to generate conditions');
      return;
    }
    
    setConditionsGenerating(true);
    setConditionsError(null);
    
    try {
      // Fetch relevant data for conditions generation
      const { data: borrowersData, error: borrowersError } = await proxyClient.get(`/api/borrower-data/borrowers/${dealNumber}`);
      
      if (borrowersError) {
        throw new Error('Failed to fetch borrowers: ' + borrowersError);
      }
      
      const { data: incomesData, error: incomesError } = await proxyClient.get(`/api/borrower-data/incomes/${dealNumber}`);
      
      if (incomesError) {
        throw new Error('Failed to fetch incomes: ' + incomesError);
      }
      
      const { data: rentalPropertiesData, error: propertiesError } = await proxyClient.get(`/api/borrower-data/rental-properties/${dealNumber}`);
      
      if (propertiesError) {
        throw new Error('Failed to fetch rental properties: ' + propertiesError);
      }
      
      const { data: dealsData, error: dealsError } = await proxyClient.get(`/api/borrower-data/deals/${dealNumber}`);
      
      if (dealsError) {
        throw new Error('Failed to fetch deal data: ' + dealsError);
      }
      
      // Generate conditions text using all fetched data
      const text = await generateConditionsText(dealsData, borrowersData, incomesData, rentalPropertiesData);
      
      // Update state with generated text
      setConditionsText(text);
      
      // Save to database
      const { error: saveError } = await proxyClient.post('/api/borrower-data/conditions', {
        deal_number: dealNumber,
        conditions_text: text
      });
      
      if (saveError) {
        console.error('Error saving conditions:', saveError);
        setConditionsError('Generated conditions but failed to save to database');
      }
      
    } catch (error: any) {
      console.error('Error generating conditions:', error);
      setConditionsError(`Error generating conditions: ${error.message}`);
    } finally {
      setConditionsGenerating(false);
    }
  };

  return (
    <Container maxWidth="lg">
      <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Top Section: Deal Number and Button - Keep this outside accordions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Deal Number"
            value={dealNumber}
            onChange={(e) => setDealNumber(e.target.value)}
            size="small"
            sx={{ minWidth: '200px' }} // Ensure minimum width
          />
          <Button 
            variant="contained" 
            onClick={handlePullInfo}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            Pull and Print Info
          </Button>
          
          {saveStatus === 'saving' && (
            <Typography variant="caption" color="text.secondary">
              Saving...
            </Typography>
          )}
          
          {saveStatus === 'saved' && (
            <Typography variant="caption" color="success.main">
              Saved
            </Typography>
          )}
        </Box>

        {/* Show error if any */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Only show accordions after pulling info */}
        {showSummary && (
          <>
            {/* SECTION ORDER CHANGED: Preliminary Check now above Deal Summary */}
            {/* Preliminary Check Accordion */}
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="preliminary-check-content"
                id="preliminary-check-header"
                sx={{ backgroundColor: 'primary.main', color: 'primary.contrastText' }} // Use theme primary color and contrast text
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleOutlineIcon sx={{ mr: 1 }} />
                  <Typography variant="subtitle1">Preliminary Check</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails> {/* Removed background color */}
                {/* New Conditional Rendering for Prelim Check */} 
                 {editMode['prelimCheck'] ? (
                    // EDIT MODE
                    <>
                      <TextField
                        label="Preliminary Check Notes"
                        multiline
                        fullWidth
                        value={prelimCheckText}
                        onChange={handlePrelimCheckChange} 
                        variant="outlined"
                        minRows={6} // Adjust rows as needed
                        sx={{ mb: 1, fontFamily: 'monospace', bgcolor: '#fffde7' }} // Changed background color to light yellow
                      />
                      {prelimCheckError && (
                         <Alert severity="error" sx={{ mb: 1 }}>{prelimCheckError}</Alert>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 1 }}>
                        <Button
                          onClick={handleSavePrelimCheck}
                          disabled={prelimCheckSaveStatus === 'saving'}
                          size="small"
                          variant="contained"
                          color="primary"
                          sx={{ mr: 1 }}
                        >
                          {prelimCheckSaveStatus === 'saving' ? <CircularProgress size={16} /> : 'Save'}
                        </Button>
                        <Button
                          onClick={handleCancelPrelimCheckEdit} 
                          size="small"
                          variant="outlined"
                          color="secondary"
                          disabled={prelimCheckSaveStatus === 'saving'} // Disable cancel while saving
                        >
                          Cancel
                        </Button>
                      </Box>
                    </>
                  ) : (
                    // READ-ONLY MODE
                    <Box sx={{
                      border: '1px solid rgba(0, 0, 0, 0.23)',
                      borderRadius: '4px',
                      p: '14px',
                      minHeight: 'calc(6 * 1.4375em + 28px)', // Match TextField approx height
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      position: 'relative',
                      bgcolor: '#fffde7', // Changed background color to light yellow
                      fontFamily: 'monospace' // Use monospace potentially?
                    }}>
                      <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {prelimCheckText || 'Preliminary check notes not generated yet.'}
                      </Typography>
                      {/* Icons Container */}
                      <Box sx={{ position: 'absolute', bottom: 8, right: 8, display: 'flex', gap: 0.5 }}>
                        <IconButton
                          disabled={!prelimCheckText}
                          size="small"
                          onClick={() => handleCopyToClipboard(prelimCheckText || '')}
                          sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                          title={prelimCheckText ? "Copy Notes" : "No Notes to Copy"}
                        >
                          <ContentCopyIcon sx={{ fontSize: '16px' }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleEditPrelimCheck} 
                          sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                          title="Edit Notes"
                        >
                          <EditIcon sx={{ fontSize: '16px' }} />
                        </IconButton>
                      </Box>
                      {/* Display save status (optional) */}
                       {prelimCheckSaveStatus === 'saving' && (
                        <Typography variant="caption" sx={{ position: 'absolute', bottom: 8, left: 8 }} color="text.secondary">
                          Saving...
                        </Typography>
                      )}
                      {prelimCheckSaveStatus === 'saved' && (
                        <Typography variant="caption" sx={{ position: 'absolute', bottom: 8, left: 8 }} color="success.main">
                          Saved
                        </Typography>
                      )}
                    </Box>
                  )}
              </AccordionDetails>
            </Accordion>

            {/* Deal Summary Accordion */}
            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="deal-summary-content"
                id="deal-summary-header"
                sx={{ backgroundColor: 'primary.main', color: 'primary.contrastText' }} // Use theme primary color and contrast text
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <SummarizeIcon sx={{ mr: 1 }} />
                  <Typography variant="subtitle1">Deal Summary</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: '#fffde7' }}> {/* Added background color */}
                {/* Conditional Rendering for Deal Summary (Re-applying) */}
                {editMode['dealSummary'] ? (
                  // EDIT MODE: Show TextField and Save/Cancel buttons
                  <>
                    <TextField
                      label="Summary Text"
                      multiline
                      fullWidth
                      value={summaryText}
                      onChange={handleSummaryChange} 
                      variant="outlined"
                      minRows={4} // Adjust rows as needed
                      sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.875rem', mb: 1, bgcolor: '#fffde7' }} // Changed background color
                    />
                    {/* Display save error if any */}
                    {saveStatus === 'error' && (
                       <Alert severity="error" sx={{ mb: 1 }}>{error || 'Failed to save summary.'}</Alert> // Use state error message
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 1 }}>
                      <Button
                        onClick={saveSummaryText} 
                        disabled={saveStatus === 'saving'}
                        size="small"
                        variant="contained"
                        color="primary"
                        sx={{ mr: 1 }}
                      >
                        {saveStatus === 'saving' ? <CircularProgress size={16} /> : 'Save'}
                      </Button>
                      <Button
                        onClick={handleCancelDealSummaryEdit} 
                        size="small"
                        variant="outlined"
                        color="secondary"
                        disabled={saveStatus === 'saving'} // Disable cancel while saving
                      >
                        Cancel
                      </Button>
                    </Box>
                  </>
                ) : (
                  // READ-ONLY MODE: Show Box with text and Copy/Edit buttons
                  <Box sx={{
                    border: '1px solid rgba(0, 0, 0, 0.23)',
                    borderRadius: '4px',
                    p: '14px',
                    minHeight: 'calc(4 * 1.4375em + 28px)', // Match TextField approx height
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    position: 'relative',
                    bgcolor: '#fffde7', // Changed background color to light yellow
                    fontFamily: 'Inter, sans-serif', // Match TextField font
                    fontSize: '0.875rem' // Match TextField font size
                  }}>
                    <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {summaryText || 'Deal summary not generated yet.'}
                    </Typography>
                    {/* Icons Container */}
                    <Box sx={{ position: 'absolute', bottom: 8, right: 8, display: 'flex', gap: 0.5 }}>
                      <IconButton
                        disabled={!summaryText}
                        size="small"
                        onClick={() => handleCopyToClipboard(summaryText || '')}
                        sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                        title={summaryText ? "Copy Summary" : "No Summary to Copy"}
                      >
                        <ContentCopyIcon sx={{ fontSize: '16px' }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={handleEditDealSummary} 
                        sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                        title="Edit Summary"
                      >
                        <EditIcon sx={{ fontSize: '16px' }} />
                      </IconButton>
                    </Box>
                     {/* Display save status */}
                     {saveStatus === 'saving' && (
                      <Typography variant="caption" sx={{ position: 'absolute', bottom: 8, left: 8 }} color="text.secondary">
                        Saving...
                      </Typography>
                    )}
                    {saveStatus === 'saved' && (
                      <Typography variant="caption" sx={{ position: 'absolute', bottom: 8, left: 8 }} color="success.main">
                        Saved
                      </Typography>
                    )}
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>

            {/* Income/Reasonability Summaries Accordion */}
            {Object.keys(irSummaries).length > 0 && (
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="ir-summary-content"
                  id="ir-summary-header"
                  sx={{ backgroundColor: 'primary.main', color: 'primary.contrastText' }} // Use theme primary color and contrast text
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AttachMoneyIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">Income/Reasonability Summaries</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 1 }}> {/* Removed background color, kept padding adjustment */}
                  {/* Nested Accordion for each borrower's I/R Summary */}
                  {Object.entries(irSummaries).map(([borrowerKey, summaryData]) => {
                    const irEditKey = `${borrowerKey}-ir`; // Unique key for edit mode state
                    return (
                      <Accordion key={irEditKey} sx={{ mb: 2, '.MuiAccordionSummary-content': { alignItems: 'center' } }}>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls={`${irEditKey}-content`}
                          id={`${irEditKey}-header`}
                        >
                          {/* Use FactCheckIcon for I/R summary */}
                          <FactCheckIcon sx={{ mr: 1 }} /> 
                          <Typography>{`I/R Summary: ${borrowerKey.replace('borrower', 'Borrower ')}`}</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.875rem', overflow: 'auto', position: 'relative', p: 2 }}>
                          {/* Original Summary Display/Edit */}
                          {editMode[irEditKey] ? (
                        <TextField
                              label="Original I/R Summary"
                          multiline
                              fullWidth
                              value={summaryData.originalSummary}
                          onChange={(e) => handleIRSummaryChange(borrowerKey, 'originalSummary', e.target.value)}
                          variant="outlined"
                              minRows={3} // Keep slightly shorter than enhanced?
                              sx={{ mb: 2, bgcolor: '#fffde7' }} // Margin below, Changed background color
                            />
                          ) : (
                             // Use a read-only box similar to enhanced summary
                             <Box sx={{
                               border: '1px solid rgba(0, 0, 0, 0.23)', // Styles from CB
                               borderRadius: '4px',
                               p: '14px',
                               minHeight: 'calc(3 * 1.4375em + 28px)', // Height from CB
                               whiteSpace: 'pre-wrap',
                               wordBreak: 'break-word',
                               position: 'relative', // Needed for icon positioning
                               bgcolor: '#fffde7', // Changed background color to light yellow
                               mb: 2, // Margin below
                             }}>
                               <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'text.secondary' }}>
                                 {summaryData.originalSummary || 'N/A'}
                               </Typography>
                               {/* Icons Container for Original Summary */}
                               {!editMode[irEditKey] && (
                                 <Box sx={{ position: 'absolute', bottom: 8, right: 8, display: 'flex', gap: 0.5 }}>
                                   {/* Copy Button (Original) */}
                                   <IconButton
                                     disabled={!summaryData.originalSummary}
                                     size="small"
                                     onClick={() => handleCopyToClipboard(summaryData.originalSummary || '')}
                                     sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                                     title={summaryData.originalSummary ? "Copy Original Summary" : "No Original Summary to Copy"}
                                   >
                                     <ContentCopyIcon sx={{ fontSize: '16px' }} />
                                   </IconButton>
                                   {/* Edit Button (triggers edit mode for both) */}
                                   <IconButton
                                     size="small"
                                     onClick={() => toggleEditMode(irEditKey)} // Toggles edit for whole section
                                     sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                                     title="Edit Summaries"
                                   >
                                     <EditIcon sx={{ fontSize: '16px' }} />
                                   </IconButton>
                                 </Box>
                               )}
                             </Box>
                          )}

                          {/* Generate Button - Keep this */}
                          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleGenerateEnhancedIR(borrowerKey)}
                              disabled={summaryData.isGenerating || !summaryData.originalSummary}
                              startIcon={summaryData.isGenerating ? <CircularProgress size={16} color="inherit" /> : null}
                          >
                              {summaryData.enhancedSummary ? 'Re-generate Enhanced I/R' : 'Generate Enhanced I/R'}
                          </Button>
                        </Box>
                        
                          {/* Enhanced Summary Display/Edit - Mirror CB Notes Structure */}
                          {editMode[irEditKey] ? (
                        <TextField
                                label="Enhanced I/R Summary"
                          multiline
                                fullWidth
                                value={summaryData.enhancedSummary}
                          onChange={(e) => handleIRSummaryChange(borrowerKey, 'enhancedSummary', e.target.value)}
                          variant="outlined"
                                minRows={4} // Match CB Notes
                                sx={{ mb: 1, bgcolor: '#fffde7' }} // Margin below text field, Changed background color
                              />
                            ) : (
                              <Box sx={{
                                border: '1px solid rgba(0, 0, 0, 0.23)', // Styles from CB
                                borderRadius: '4px',
                                p: '14px',
                                minHeight: 'calc(4 * 1.4375em + 28px)', // Height from CB
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                position: 'relative', // Ensure Box is the positioning parent
                                bgcolor: '#fffde7', // Changed background color to light yellow
                              }}>
                                <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                  {summaryData.enhancedSummary || 'Generate enhanced summary.'}
                                </Typography>
                                 {/* Icons Container - Structure copied from CB Notes */}
                                 {!editMode[irEditKey] && summaryData.enhancedSummary && (
                                   <Box sx={{ position: 'absolute', bottom: 8, right: 8, display: 'flex', gap: 0.5 }}>
                                     {/* Copy Button */}
                                     <IconButton
                                       disabled={!summaryData.enhancedSummary} // Disable if no enhanced summary to copy
                                       size="small"
                                       onClick={() => handleCopyToClipboard(summaryData.enhancedSummary || '')} // Use summaryData, provide fallback
                                       sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                                       title={summaryData.enhancedSummary ? "Copy Enhanced Summary" : "No Enhanced Summary to Copy"}
                                     >
                                       <ContentCopyIcon sx={{ fontSize: '16px' }} />
                                     </IconButton>
                                     {/* Edit Button */}
                                     <IconButton
                                       size="small"
                                       onClick={() => toggleEditMode(irEditKey)} // Use irEditKey
                                       sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                                       title="Edit Summaries"
                                     >
                                       <EditIcon sx={{ fontSize: '16px' }} />
                                     </IconButton>
                                   </Box>
                        )}
                      </Box>
                            )}
                          
                          {/* Save/Cancel Controls - Only show if in edit mode */}
                          {editMode[irEditKey] && (
                             <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 1 }}>
                               {/* Save Button */}
                               <Button
                                 onClick={() => handleSaveIRSummary(borrowerKey)} // Use correct save function
                                 // disabled={summaryData.isSaving} // Add isSaving state if needed
                                 size="small"
                                 variant="contained"
                                 color="primary"
                                 sx={{ mr: 1 }}
                               >
                                 Save
                               </Button>
                               {/* Cancel Button */}
                               <Button
                                 onClick={() => toggleEditMode(irEditKey)} // Use correct key
                                 size="small"
                                 variant="outlined"
                                 color="secondary"
                               >
                                 Cancel
                               </Button>
                             </Box>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            )}

            {/* --- NEW: Rental Income Reasonability Accordion --- */}
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="rental-reasonability-content"
                id="rental-reasonability-header"
                sx={{ backgroundColor: 'primary.main', color: 'primary.contrastText' }} // Use theme primary color and contrast text
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <SummarizeIcon sx={{ mr: 1 }} /> {/* Placeholder Icon */} 
                  <Typography variant="subtitle1">Rental Income Reasonability</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.875rem', p: 2 }}> {/* Removed background color */}
                {editMode['rentalReasonability'] ? (
                  // EDIT MODE
                  <>
                    <TextField
                      label="Original Rental Income Notes"
                      multiline fullWidth
                      value={rentalReasonabilityOriginal}
                      onChange={handleRentalReasonabilityChange('original')}
                      variant="outlined" minRows={3} sx={{ mb: 2, bgcolor: '#fffde7' }} // Changed background color
                    />
                    <TextField
                      label="Enhanced Rental Income Notes"
                      multiline fullWidth
                      value={rentalReasonabilityEnhanced}
                      onChange={handleRentalReasonabilityChange('enhanced')}
                      variant="outlined" minRows={3} sx={{ mb: 1, bgcolor: '#fffde7' }} // Changed background color
                    />
                    {rentalReasonabilityError && (
                       <Alert severity="error" sx={{ mb: 1 }}>{rentalReasonabilityError}</Alert>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 1 }}>
                      <Button onClick={handleSaveRentalReasonability} disabled={rentalReasonabilitySaveStatus === 'saving'} size="small" variant="contained" color="primary" sx={{ mr: 1 }}>
                        {rentalReasonabilitySaveStatus === 'saving' ? <CircularProgress size={16}/> : 'Save'}
                      </Button>
                      <Button onClick={handleCancelRentalReasonabilityEdit} size="small" variant="outlined" color="secondary" disabled={rentalReasonabilitySaveStatus === 'saving'}>
                        Cancel
                      </Button>
                    </Box>
                  </>
                ) : (
                  // READ-ONLY MODE
                  <>
                    {/* Original Notes Display */} 
                    <Box sx={{ border: '1px solid rgba(0,0,0,0.23)', borderRadius: 1, p: 1.5, mb: 2, position: 'relative', bgcolor: '#fffde7' }}> {/* Changed background color */}
                      <Typography variant="caption" display="block" gutterBottom sx={{ fontWeight: 'bold' }}>Original Generated Notes:</Typography>
                      <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', minHeight: '3em', color: 'text.secondary' }}>
                         {rentalReasonabilityOriginal || 'Generate notes...'}
                       </Typography>
                      <Box sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 0.5 }}>
                        <IconButton size="small" onClick={() => handleCopyToClipboard(rentalReasonabilityOriginal || '')} disabled={!rentalReasonabilityOriginal} title="Copy Original"><ContentCopyIcon sx={{ fontSize: '16px' }} /></IconButton>
                        <IconButton size="small" onClick={handleEditRentalReasonability} title="Edit Notes"><EditIcon sx={{ fontSize: '16px' }} /></IconButton>
                      </Box>
                    </Box>
                    
                    {/* Generate Button */} 
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                      <Button 
                        variant="outlined" 
                        size="small" 
                        onClick={handleGenerateEnhancedRentalReasonability} 
                        disabled={rentalReasonabilityGenerating || !rentalReasonabilityOriginal} 
                        startIcon={rentalReasonabilityGenerating ? <CircularProgress size={16}/> : null}
                      >
                        {rentalReasonabilityEnhanced ? 'Re-generate Enhanced Notes' : 'Generate Enhanced Notes'}
                      </Button>
                    </Box>

                    {/* Enhanced Notes Display */} 
                    <Box sx={{ border: '1px solid rgba(0,0,0,0.23)', borderRadius: 1, p: 1.5, position: 'relative', bgcolor: '#fffde7' }}> {/* Changed background color */}
                      <Typography variant="caption" display="block" gutterBottom sx={{ fontWeight: 'bold' }}>Enhanced Notes:</Typography>
                      <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', minHeight: '3em' }}>
                         {rentalReasonabilityEnhanced || 'Generate enhanced notes...'}
                       </Typography>
                      <Box sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 0.5 }}>
                        <IconButton size="small" onClick={() => handleCopyToClipboard(rentalReasonabilityEnhanced || '')} disabled={!rentalReasonabilityEnhanced} title="Copy Enhanced"><ContentCopyIcon sx={{ fontSize: '16px' }} /></IconButton>
                        <IconButton size="small" onClick={handleEditRentalReasonability} title="Edit Notes"><EditIcon sx={{ fontSize: '16px' }} /></IconButton>
                      </Box>
                      {/* Status indicators */} 
                      {rentalReasonabilitySaveStatus === 'saving' && <Typography variant="caption" sx={{ position: 'absolute', bottom: 4, left: 8 }} color="text.secondary">Saving...</Typography>}
                      {rentalReasonabilitySaveStatus === 'saved' && <Typography variant="caption" sx={{ position: 'absolute', bottom: 4, left: 8 }} color="success.main">Saved</Typography>}
                    </Box>
                  </>
                )}
              </AccordionDetails>
            </Accordion>

            {/* --- NEW: Downpayment Reasonability Accordion --- */}
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="downpayment-reasonability-content"
                id="downpayment-reasonability-header"
                sx={{ backgroundColor: 'primary.main', color: 'primary.contrastText' }} // Use theme primary color and contrast text
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AttachMoneyIcon sx={{ mr: 1 }} /> {/* Placeholder Icon */} 
                  <Typography variant="subtitle1">Downpayment Reasonability</Typography>
                </Box>
              </AccordionSummary>
               <AccordionDetails sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.875rem', p: 2 }}> {/* Removed background color */}
                 {/* Add a React fragment to fix the children error */}
                 {(() => {
                   console.log('Rendering Downpayment Reasonability with values:', {
                     original: downpaymentReasonabilityOriginal,
                     enhanced: downpaymentReasonabilityEnhanced,
                     editMode: editMode['downpaymentReasonability']
                   });
                   return null;
                 })()}
                 
                 {editMode['downpaymentReasonability'] ? (
                   // EDIT MODE
                   <>
                     <TextField
                       label="Original Downpayment Notes"
                       multiline fullWidth
                       value={downpaymentReasonabilityOriginal}
                       onChange={handleDownpaymentReasonabilityChange('original')}
                       variant="outlined" minRows={3} sx={{ mb: 2, bgcolor: '#fffde7' }} // Changed background color
                     />
                     <TextField
                       label="Enhanced Downpayment Notes"
                       multiline fullWidth
                       value={downpaymentReasonabilityEnhanced}
                       onChange={handleDownpaymentReasonabilityChange('enhanced')}
                       variant="outlined" minRows={3} sx={{ mb: 1, bgcolor: '#fffde7' }} // Changed background color
                     />
                     {downpaymentReasonabilityError && (
                        <Alert severity="error" sx={{ mb: 1 }}>{downpaymentReasonabilityError}</Alert>
                     )}
                     <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 1 }}>
                       <Button onClick={handleSaveDownpaymentReasonability} disabled={downpaymentReasonabilitySaveStatus === 'saving'} size="small" variant="contained" color="primary" sx={{ mr: 1 }}>
                         {downpaymentReasonabilitySaveStatus === 'saving' ? <CircularProgress size={16}/> : 'Save'}
                       </Button>
                       <Button onClick={handleCancelDownpaymentReasonabilityEdit} size="small" variant="outlined" color="secondary" disabled={downpaymentReasonabilitySaveStatus === 'saving'}>
                         Cancel
                       </Button>
                     </Box>
                   </>
                 ) : (
                   // READ-ONLY MODE
                   <>
                     {/* Original Notes Display */} 
                     <Box sx={{ border: '1px solid rgba(0,0,0,0.23)', borderRadius: 1, p: 1.5, mb: 2, position: 'relative', bgcolor: '#fffde7' }}> {/* Changed background color */}
                       <Typography variant="caption" display="block" gutterBottom sx={{ fontWeight: 'bold' }}>Original Generated Notes:</Typography>
                       <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', minHeight: '3em', color: 'text.secondary' }}>
                          {downpaymentReasonabilityOriginal || 'No downpayment data available'}
                          {!downpaymentReasonabilityOriginal && <span style={{color: 'red'}}> (State is empty despite API call succeeding)</span>}
                        </Typography>
                       <Box sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 0.5 }}>
                         <IconButton size="small" onClick={() => handleCopyToClipboard(downpaymentReasonabilityOriginal || '')} disabled={!downpaymentReasonabilityOriginal} title="Copy Original"><ContentCopyIcon sx={{ fontSize: '16px' }} /></IconButton>
                         <IconButton size="small" onClick={handleEditDownpaymentReasonability} title="Edit Notes"><EditIcon sx={{ fontSize: '16px' }} /></IconButton>
                       </Box>
                     </Box>
                     
                     {/* Generate Button */} 
                     <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                       <Button variant="outlined" size="small" onClick={handleGenerateEnhancedDownpaymentReasonability} disabled={downpaymentReasonabilityGenerating || !downpaymentReasonabilityOriginal} startIcon={downpaymentReasonabilityGenerating ? <CircularProgress size={16}/> : null}>
                          {downpaymentReasonabilityEnhanced ? 'Re-generate Enhanced Notes' : 'Generate Enhanced Notes'}
                        </Button>
                      </Box>

                     {/* Enhanced Notes Display */} 
                     <Box sx={{ border: '1px solid rgba(0,0,0,0.23)', borderRadius: 1, p: 1.5, position: 'relative', bgcolor: '#fffde7' }}> {/* Changed background color */}
                       <Typography variant="caption" display="block" gutterBottom sx={{ fontWeight: 'bold' }}>Enhanced Notes:</Typography>
                       <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', minHeight: '3em' }}>
                          {downpaymentReasonabilityEnhanced || 'Generate enhanced notes...'}
                        </Typography>
                       <Box sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 0.5 }}>
                         <IconButton size="small" onClick={() => handleCopyToClipboard(downpaymentReasonabilityEnhanced || '')} disabled={!downpaymentReasonabilityEnhanced} title="Copy Enhanced"><ContentCopyIcon sx={{ fontSize: '16px' }} /></IconButton>
                         <IconButton size="small" onClick={handleEditDownpaymentReasonability} title="Edit Notes"><EditIcon sx={{ fontSize: '16px' }} /></IconButton>
                       </Box>
                       {/* Status indicators */} 
                       {downpaymentReasonabilitySaveStatus === 'saving' && <Typography variant="caption" sx={{ position: 'absolute', bottom: 4, left: 8 }} color="text.secondary">Saving...</Typography>}
                       {downpaymentReasonabilitySaveStatus === 'saved' && <Typography variant="caption" sx={{ position: 'absolute', bottom: 4, left: 8 }} color="success.main">Saved</Typography>}
                     </Box>
                   </>
                 )}
               </AccordionDetails>
            </Accordion>

            {/* Credit Bureau Notes Accordion */}
            {Object.keys(borrowersNotes).length > 0 && (
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="cb-notes-content"
                  id="cb-notes-header"
                  sx={{ backgroundColor: 'primary.main', color: 'primary.contrastText' }} // Use theme primary color and contrast text
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AssignmentIndIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">Credit Bureau Notes</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails> {/* Removed background color */}
                  {Object.entries(borrowersNotes).map(([borrowerKey, noteData]) => {
                    return (
                      <Accordion key={borrowerKey} sx={{ mb: 2 }}>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls={`${borrowerKey}-content`}
                          id={`${borrowerKey}-header`}
                        >
                          <AssignmentIndIcon sx={{ mr: 1 }} />
                          <Typography>{`Borrower CB Notes: ${borrowerKey.replace('borrower', 'Borrower ')}`}</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.875rem', overflow: 'auto', position: 'relative', p: 2 }}>
                          {/* Original Notes Display/Edit */}
                          <Typography variant="caption" display="block" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Original Generated Notes:
                        </Typography>
                          {editMode[borrowerKey] ? (
                        <TextField
                              label="Original CB Notes"
                          multiline
                              fullWidth
                              value={noteData.originalNotes}
                          onChange={(e) => handleBorrowerNotesChange(borrowerKey, 'originalNotes', e.target.value)}
                          variant="outlined"
                              minRows={3}
                              sx={{ mb: 2, bgcolor: '#fffde7' }} // Changed background color
                            />
                          ) : (
                            <Box sx={{
                              border: '1px solid rgba(0, 0, 0, 0.23)',
                              borderRadius: '4px',
                              p: '14px',
                              minHeight: 'calc(3 * 1.4375em + 28px)',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              position: 'relative',
                              bgcolor: '#fffde7', // Changed background color to light yellow
                              mb: 2,
                            }}>
                              <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'text.secondary' }}>
                                {noteData.originalNotes || 'N/A'}
                        </Typography>
                              {!editMode[borrowerKey] && (
                                <Box sx={{ position: 'absolute', bottom: 8, right: 8, display: 'flex', gap: 0.5 }}>
                                  <IconButton
                                    disabled={!noteData.originalNotes}
                                    size="small"
                                    onClick={() => handleCopyToClipboard(noteData.originalNotes || '')}
                                    sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                                    title={noteData.originalNotes ? "Copy Original Notes" : "No Original Notes to Copy"}
                                  >
                                    <ContentCopyIcon sx={{ fontSize: '16px' }} />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => toggleEditMode(borrowerKey)}
                                    sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                                    title="Edit Notes"
                                  >
                                    <EditIcon sx={{ fontSize: '16px' }} />
                                  </IconButton>
                                </Box>
                              )}
                            </Box>
                          )}

                          {/* Generate Button - Modified */}
                          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleGenerateEnhancedCBNotes(borrowerKey)} // Use new handler
                              disabled={noteData.isSaving || !noteData.originalNotes} // Disable if saving or no original notes
                              startIcon={noteData.isSaving ? <CircularProgress size={16} color="inherit" /> : null}
                            >
                              {/* Dynamic Text: Generate or Re-generate */}
                              {noteData.enhancedNotes ? 'Re-generate Enhanced Notes' : 'Generate Enhanced Notes'}
                            </Button>
                          </Box>

                          {/* Enhanced Notes Display/Edit */}
                          {editMode[borrowerKey] ? (
                        <TextField
                              label="Enhanced CB Notes"
                          multiline
                              fullWidth
                              value={noteData.enhancedNotes}
                          onChange={(e) => handleBorrowerNotesChange(borrowerKey, 'enhancedNotes', e.target.value)}
                          variant="outlined"
                              minRows={4}
                              sx={{ mb: 1, bgcolor: '#fffde7' }} // Changed background color
                            />
                          ) : (
                            <Box sx={{
                              border: '1px solid rgba(0, 0, 0, 0.23)',
                              borderRadius: '4px',
                              p: '14px',
                              minHeight: 'calc(4 * 1.4375em + 28px)',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              position: 'relative',
                              bgcolor: '#fffde7', // Changed background color to light yellow
                            }}>
                              <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {noteData.enhancedNotes || 'Generate enhanced notes.'}
                          </Typography>
                              {!editMode[borrowerKey] && noteData.enhancedNotes && (
                                <Box sx={{ position: 'absolute', bottom: 8, right: 8, display: 'flex', gap: 0.5 }}>
                                  <IconButton
                                    disabled={!noteData.enhancedNotes}
                                    size="small"
                                    onClick={() => handleCopyToClipboard(noteData.enhancedNotes || '')}
                                    sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                                    title={noteData.enhancedNotes ? "Copy Enhanced Notes" : "No Enhanced Notes to Copy"}
                                  >
                                    <ContentCopyIcon sx={{ fontSize: '16px' }} />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => toggleEditMode(borrowerKey)}
                                    sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                                    title="Edit Notes"
                                  >
                                    <EditIcon sx={{ fontSize: '16px' }} />
                                  </IconButton>
                                </Box>
                          )}
                            </Box>
                          )}

                          {/* Save/Cancel Controls */}
                          {editMode[borrowerKey] && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 1 }}>
                              <Button
                                onClick={() => handleSaveEnhancedNotes(borrowerKey)}
                                disabled={noteData.isSaving}
                                size="small"
                                variant="contained"
                                color="primary"
                                sx={{ mr: 1 }}
                              >
                                Save
                              </Button>
                              <Button
                                onClick={() => toggleEditMode(borrowerKey)}
                                size="small"
                                variant="outlined"
                                color="secondary"
                              >
                                Cancel
                              </Button>
                      </Box>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            )}
            
            {/* New Conditions Accordion */}
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="conditions-content"
                id="conditions-header"
                sx={{ backgroundColor: 'primary.main', color: 'primary.contrastText' }} // Use theme primary color and contrast text
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FactCheckIcon sx={{ mr: 1 }} />
                  <Typography variant="subtitle1">Conditions</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails> {/* Removed background color */}
                {editingConditions ? (
                  // EDIT MODE
                  <>
                    <TextField
                      label="Conditions"
                      multiline
                      fullWidth
                      value={conditionsText}
                      onChange={handleConditionsChange}
                      variant="outlined"
                      minRows={10}
                      sx={{ mb: 1, fontFamily: 'monospace', bgcolor: '#fffde7' }}
                    />
                    {conditionsError && (
                      <Alert severity="error" sx={{ mb: 1 }}>{conditionsError}</Alert>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 1 }}>
                      <Button
                        onClick={handleSaveConditions}
                        disabled={conditionsSaveStatus === 'saving'}
                        size="small"
                        variant="contained"
                        color="primary"
                        sx={{ mr: 1 }}
                      >
                        {conditionsSaveStatus === 'saving' ? <CircularProgress size={16} /> : 'Save'}
                      </Button>
                      <Button
                        onClick={handleCancelConditionsEdit}
                        size="small"
                        variant="outlined"
                        color="secondary"
                        disabled={conditionsSaveStatus === 'saving'}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </>
                ) : (
                  // VIEW MODE
                  <>
                    <Box sx={{ position: 'relative', mb: 2 }}>
                      {/* Copy button and Edit button */}
                      <IconButton 
                        size="small" 
                        sx={{ position: 'absolute', top: 0, right: 36 }}
                        onClick={() => handleCopyToClipboard(conditionsText)}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        sx={{ position: 'absolute', top: 0, right: 0 }}
                        onClick={handleEditConditions}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      
                      {/* Display conditions text */}
                      <Box sx={{ 
                        pl: 2, 
                        pr: 2, 
                        pt: 4, 
                        pb: 2, 
                        borderRadius: 1, 
                        bgcolor: '#fffde7', 
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'monospace', 
                        fontSize: '0.875rem',
                        minHeight: '100px'
                      }}>
                        {conditionsText || 'No conditions generated yet. Click "Generate Conditions" to create conditions.'}
                      </Box>
                      
                      {/* Display save status */}
                      {conditionsSaveStatus === 'saving' && (
                        <Typography variant="caption" sx={{ position: 'absolute', bottom: 8, left: 8 }} color="text.secondary">
                          Saving...
                        </Typography>
                      )}
                      {conditionsSaveStatus === 'saved' && (
                        <Typography variant="caption" sx={{ position: 'absolute', bottom: 8, left: 8 }} color="success.main">
                          Saved
                        </Typography>
                      )}
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleGenerateConditions}
                        disabled={conditionsGenerating}
                        startIcon={conditionsGenerating ? <CircularProgress size={16} color="inherit" /> : null}
                      >
                        {conditionsText ? 'Re-generate Conditions' : 'Generate Conditions'}
                      </Button>
                    </Box>
                  </>
                )}
              </AccordionDetails>
            </Accordion>

            {/* --- NEW: Rental Worksheet Accordion --- */}
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="rental-worksheet-content"
                id="rental-worksheet-header"
                sx={{ backgroundColor: 'primary.main', color: 'primary.contrastText' }} // Use theme primary color and contrast text
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AssessmentIcon sx={{ mr: 1 }} /> {/* Added AssessmentIcon */}
                  <Typography variant="subtitle1">Rental Worksheet</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {rentalReasonabilityOriginal ? (
                  <Box
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontFamily: 'monospace',
                      p: 2,
                      border: '1px solid rgba(0, 0, 0, 0.12)',
                      borderRadius: 1,
                      bgcolor: '#fffde7',
                      position: 'relative',
                    }}
                  >
                    <Typography
                      variant="body2"
                      component="div"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {generateRentalWorksheetData()}
                    </Typography>
                    <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleCopyToClipboard(generateRentalWorksheetData())}
                        title="Copy Worksheet Data"
                      >
                        <ContentCopyIcon sx={{ fontSize: '16px' }} />
                      </IconButton>
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Rental worksheet data will appear here when Rental Income Reasonability is generated.
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
            {/* --- End Rental Worksheet Accordion --- */}
            
          </>
        )}
      </Paper>
      
      {/* Status snackbar */}
      <Snackbar 
        open={saveStatus === 'error'} 
        autoHideDuration={6000} 
        onClose={() => setSaveStatus('idle')}
      >
        <Alert severity="error" onClose={() => setSaveStatus('idle')}>
          Failed to save summary text. Please try again.
        </Alert>
      </Snackbar>

      {/* Snackbar for Copy feedback */}
      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={2000} // Shorter duration for copy feedback
        onClose={() => setCopySnackbarOpen(false)}
        message="Copied to clipboard!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      <ChatWidget />
    </Container>
  );
};

export default UWNotesPage; 